import { ChevronDown } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import {
  MoreHorizontal,
  Send,
  Paperclip,
  AtSign,
  X,
  FileText,
  Search,
  AlertTriangle,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Icons } from "../components/icons";
import {
  useFloating,
  autoUpdate,
  flip,
  shift,
  size,
  FloatingPortal,
} from "@floating-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useKanban from "../hooks/useKanban";
import useProject from "../hooks/useProject";
import { getMaxEstimatedHours } from "../lib/taskHours";
import { useLocation, useParams } from "react-router-dom";
import projectService from "../services/projectService";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import SortableList from "../components/Kanban/SortableList";
import SortableCard from "../components/Kanban/SortableCard";

// ── Helpers (unchanged) ──────────────────────────────────────────────
const getInitials = (name = "") => name.trim().charAt(0).toUpperCase() || "?";

// Places the comment panel next to the task card that opened it rather than
// centring it on screen. Prefers the card's right side; flips left when that
// would overflow, and falls back to centring when neither side fits (narrow
// screens) or when no anchor was captured. Vertically it aligns to the card's
// top, then clamps so the panel never leaves the viewport.
const COMMENT_PANEL = { width: 447, height: 307, gap: 12, margin: 8 };

// ── Unseen-comment tracking ──────────────────────────────────────────
// There is no server-side read receipt on comments (the Task schema stores
// only author/text/mentions/attachments + timestamps), so "seen" is tracked
// locally: the timestamp at which this user last opened each task's comments
// is kept in localStorage and compared against the newest comment's createdAt.
// Keyed by user so separate accounts on one browser don't share read state.
const COMMENTS_SEEN_KEY = "kanbanCommentsSeen";

const readSeenMap = (userId) => {
  if (!userId || typeof window === "undefined") return {};
  try {
    const all = JSON.parse(localStorage.getItem(COMMENTS_SEEN_KEY) || "{}");
    return all[userId] || {};
  } catch {
    return {}; // corrupt/unavailable storage must never break the board
  }
};

const writeSeenAt = (userId, taskId, isoTime) => {
  if (!userId || !taskId || typeof window === "undefined") return;
  try {
    const all = JSON.parse(localStorage.getItem(COMMENTS_SEEN_KEY) || "{}");
    all[userId] = { ...(all[userId] || {}), [taskId]: isoTime };
    localStorage.setItem(COMMENTS_SEEN_KEY, JSON.stringify(all));
  } catch {
    /* storage full or blocked — the dot just stays visible */
  }
};

// A task has unseen comments when its newest comment is newer than the last
// time this user opened it, and that comment was written by someone else.
const hasUnseenComments = (task, userId, seenMap) => {
  const comments = task?.comments;
  if (!comments?.length || !userId) return false;

  let newest = null;
  let newestAuthorId = null;
  for (const c of comments) {
    const t = c?.createdAt ? new Date(c.createdAt).getTime() : NaN;
    if (Number.isNaN(t)) continue;
    if (newest === null || t > newest) {
      newest = t;
      newestAuthorId = String(c.author?._id || c.author || "");
    }
  }
  if (newest === null) return false;

  // Your own comment should never mark the task as unread for you.
  if (newestAuthorId && newestAuthorId === String(userId)) return false;

  const seenAt = seenMap[task._id];
  if (!seenAt) return true; // never opened → anything present is unseen
  return newest > new Date(seenAt).getTime();
};

// Positions the hover list of assignees. It is portalled to <body> (the card
// clips its children), so it is placed from the avatar stack's viewport rect:
// directly below the stack and right-aligned to it, flipping above when there
// is no room below, and clamped horizontally so it never leaves the screen.
const ASSIGNEE_POPUP = { width: 220, rowHeight: 44, padding: 24, margin: 8 };

const getAssigneePopupStyle = (rect, count = 0) => {
  if (!rect || typeof window === "undefined") return {};
  const { width, rowHeight, padding, margin } = ASSIGNEE_POPUP;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const height = Math.min(240, count * rowHeight + padding);

  // Right-align to the avatar stack, then clamp inside the viewport.
  let left = rect.right - width;
  left = Math.min(Math.max(margin, left), vw - width - margin);

  // Prefer below the stack; flip above when it would overflow the bottom.
  const below = rect.bottom + 6;
  const top =
    below + height + margin <= vh
      ? below
      : Math.max(margin, rect.top - 6 - height);

  return { position: "fixed", left, top };
};

const getCommentPanelStyle = (rect) => {
  if (!rect || typeof window === "undefined") return {};
  const { width, height, gap, margin } = COMMENT_PANEL;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Not enough room either side (mobile) — keep the centred behaviour.
  if (vw < width + gap * 2) return {};

  const fitsRight = rect.right + gap + width + margin <= vw;
  const left = fitsRight
    ? rect.right + gap
    : Math.max(margin, rect.left - gap - width);

  const maxTop = Math.max(margin, vh - height - margin);
  const top = Math.min(Math.max(margin, rect.top), maxTop);

  return { position: "fixed", left, top, margin: 0 };
};

const getProfileImageUrl = (member) => {
  if (!member) return "";
  if (typeof member.profilePicture === "string") return member.profilePicture;
  if (member.profilePicture?.url) return member.profilePicture.url;
  if (typeof member.avatar === "string") return member.avatar;
  if (typeof member.profileImage === "string") return member.profileImage;
  return "";
};
const CommentAvatar = ({ author, size = 40 }) => {
  const img = getProfileImageUrl(author);
  const name = author?.name || author || "U";
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-full flex items-center justify-center overflow-hidden text-sm font-semibold bg-[#F3F4F6] text-[#7F7F7F] flex-shrink-0"
    >
      {img ? (
        <img src={img} alt={name} className="w-full h-full object-cover" />
      ) : (
        getInitials(name)
      )}
    </div>
  );
};

const getLoggedInUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

// Activity log timestamp — "12 Sep 2024 · 12:33 AM"
const formatLogTimestamp = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = d.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} · ${timePart}`;
};

// Extract a human-readable message from a failed request (axios / thunk / Error).
const getErrorMessage = (
  err,
  fallback = "Something went wrong. Please try again.",
) =>
  // A rejected Redux thunk's .unwrap() throws the exact value passed to
  // rejectWithValue() — usually a plain string, not an Error/axios object.
  (typeof err === "string" && err) ||
  err?.response?.data?.message ||
  err?.payload ||
  err?.message ||
  fallback;

// Human-readable file size (bytes → B/KB/MB/GB), matching the precision
// shown in the design (e.g. "1.2 MB").
const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

// Only allow http(s) links for stored attachment URLs — blocks
// javascript:/data: URIs (stored XSS) without affecting normal files.
const getSafeUrl = (url) => {
  if (typeof url !== "string") return undefined;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? url
      : undefined;
  } catch {
    return undefined;
  }
};

// Normalize a picked date to noon UTC of its calendar day so the picker's
// click-time stamp can't make a valid end date compare as before the start.
const toDateOnlyISO = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return new Date(
    Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 12),
  ).toISOString();
};

// ── Per-user board ordering ─────────────────────────────────────────
// List/card order is a personal preference: stored locally per user +
// project (never sent to the backend), so rearranging the board only
// affects this account and survives refreshes permanently.
const getBoardOrderKey = (projectId) => {
  const userId = getLoggedInUser()?._id || "guest";
  return `qwickhub_kanban_order_${userId}_${projectId}`;
};

const loadBoardOrder = (projectId) => {
  try {
    return JSON.parse(localStorage.getItem(getBoardOrderKey(projectId)));
  } catch {
    return null;
  }
};

const saveBoardOrder = (projectId, cols) => {
  try {
    localStorage.setItem(
      getBoardOrderKey(projectId),
      JSON.stringify({
        lists: cols.map((c) => c._id),
        tasks: Object.fromEntries(
          cols.map((c) => [c._id, (c.tasks || []).map((t) => t._id)]),
        ),
      }),
    );
  } catch {
    /* storage unavailable — ordering just won't persist */
  }
};

// Stable-sort items by a saved id order; ids not in the saved order keep
// their backend order and go to the end (new lists/cards appear last).
const orderBySaved = (items = [], savedIds) => {
  if (!savedIds?.length) return items;
  const pos = new Map(savedIds.map((id, i) => [id, i]));
  return [...items].sort((a, b) => {
    const pa = pos.has(a._id) ? pos.get(a._id) : Infinity;
    const pb = pos.has(b._id) ? pos.get(b._id) : Infinity;
    return pa - pb;
  });
};

const boardOrderSignature = (cols) =>
  cols
    .map((c) => `${c._id}:${(c.tasks || []).map((t) => t._id).join(",")}`)
    .join("|");

const getPriorityBadge = (priority) => {
  const p = (priority || "").toUpperCase();

  if (p === "LOW") {
    return "bg-[#CFFFDE] text-[#003C18]";
  }

  if (p === "MEDIUM") {
    return "bg-[#FFE8B6] text-[#B76E00]";
  }

  if (p === "HIGH") {
    return "bg-[#FFCA75CC] text-[#FF1A00]";
  }

  if (p === "CRITICAL") {
    return "bg-[#FFD6D6] text-[#D60000]";
  }

  return "bg-blue-100 text-blue-700";
};

const formatPriority = (priority) => {
  if (!priority) return "";
  return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
};
const formatRelativeTime = (dateString) => {
  if (!dateString) return "";
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin} min${diffMin !== 1 ? "s" : ""} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? "s" : ""} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`;

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const Kanban = () => {
  const { setColumns: setOutletColumns } = useOutletContext();
  const navigate = useNavigate();

  const getTaskCount = (project) => {
    return (
      project.lists?.reduce(
        (count, list) => count + (list.tasks?.length || 0),
        0,
      ) || 0
    );
  };

  // ── Redux hooks ──────────────────────────────────────────────────
  const {
    projects,
    selectedProject,
    columns,
    isCreatingTask,
    isEditingTask,
    isPostingComment,
    loadProjects,
    loadBoard,
    selectProject,
    clearBoard,
    updateColumns,
    addList,
    addTask,
    updateTask,
    removeTask,
    toggleStatus,
    addSubtask,
    toggleSubtask,
    sendComment,
    updateSubtask,
    deleteSubtask,
    updateComment,
    deleteComment,
    addSubtaskAttachment,
    deleteSubtaskAttachment,
    logs,
    logsProject,
    logsPagination,
    isLogsLoading,
    isDeletingLogs,
    logsError,
    loadProjectLogs,
    removeProjectLogs,
    clearLogs,
  } = useKanban();

  const { clearUsers, allUsers, loadAllUsers } = useProject();
  const location = useLocation();

  const [members, setMembers] = useState([]);
  const [errorModal, setErrorModal] = useState(null);

  // Unseen-comment dots. `commentsSeen` mirrors localStorage in state so the
  // dot disappears immediately when a task's comments are opened.
  const currentUserId = useSelector((s) => s.auth.user?._id) || null;
  const [commentsSeen, setCommentsSeen] = useState(() =>
    readSeenMap(currentUserId),
  );
  useEffect(() => {
    setCommentsSeen(readSeenMap(currentUserId));
  }, [currentUserId]);

  // Records "user has now seen every comment on this task" and clears its dot.
  const markCommentsSeen = (task) => {
    if (!task?._id || !currentUserId) return;
    const now = new Date().toISOString();
    writeSeenAt(currentUserId, task._id, now);
    setCommentsSeen((prev) => ({ ...prev, [task._id]: now }));
  };

  const [openAssigneePopup, setOpenAssigneePopup] = useState(null);
  // Viewport rect of the hovered assignee stack. The popup is portalled to
  // <body> to escape the card's overflow-hidden, so it needs absolute
  // coordinates rather than CSS-relative positioning.
  const [assigneePopupRect, setAssigneePopupRect] = useState(null);
  useEffect(() => {
    setMembers(selectedProject?.members || []);
  }, [selectedProject]);
  const [showComments, setShowComments] = useState(false);
  // Viewport rect of the task card whose comment icon was clicked. The comment
  // panel is positioned against this instead of being centred on screen, so it
  // reads as belonging to that specific card.
  const [commentAnchorRect, setCommentAnchorRect] = useState(null);

  const [showTaskMentionBox, setShowTaskMentionBox] = useState(false);
  const [taskMentionSearch, setTaskMentionSearch] = useState("");

  const handleTaskCommentChange = (e) => {
    const value = e.target.value;

    setNewComment(value);

    const lastAtIndex = value.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const afterAt = value.substring(lastAtIndex + 1);

      if (!afterAt.includes(" ") && !afterAt.includes("\n")) {
        setTaskMentionSearch(afterAt);
        setShowTaskMentionBox(true);
      } else {
        setShowTaskMentionBox(false);
      }
    } else {
      setShowTaskMentionBox(false);
    }
  };

  const getTaskMentionUsers = () => {
    // Only members assigned to this project (normalized flat user objects)
    const users = projectMembers;

    if (!taskMentionSearch) return users;

    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(taskMentionSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(taskMentionSearch.toLowerCase()),
    );
  };

  const insertTaskMention = (user) => {
    const lastAtIndex = newComment.lastIndexOf("@");

    const before = newComment.substring(0, lastAtIndex);

    const after = newComment.substring(
      lastAtIndex + taskMentionSearch.length + 1,
    );
    setNewComment(`${before}@${user.name} ${after}`);

    setShowTaskMentionBox(false);
    setTaskMentionSearch("");

    setMentionedUserIds((prev) =>
      prev.includes(user._id) ? prev : [...prev, user._id],
    );
  };

  // ── Fetch on mount ───────────────────────────────────────────────
  useEffect(() => {
    clearUsers();
    loadProjects();
    loadAllUsers(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { projectRef } = useParams();

  const projectId = projectRef?.split("-").pop();
  useEffect(() => {
    if (location.state?.project) {
      selectProject(location.state.project);
    }
  }, [location.state?.project?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!projectId) {
      // Arriving at bare /dashboard/kanban: if a board is still selected
      // (user navigated to another tab and came back), restore its URL.
      // The in-board Back button clears the selection BEFORE navigating
      // here, so after Back this shows the main list as expected.
      if (selectedProject?._id) {
        navigate(`/dashboard/kanban/${selectedProject._id}`, {
          replace: true,
        });
      } else {
        clearBoard();
      }
      return;
    }
    if (projects?.length > 0) {
      const project = projects.find((p) => p._id === projectId);
      if (project) {
        selectProject(project);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projects]);
  // ── Fetch board when project selected ────────────────────────────
  useEffect(() => {
    if (!selectedProject) return;
    loadBoard(selectedProject._id);
  }, [selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch the full project record for its start/deadline dates ────
  // The board's project objects can omit startDate/deadline; the task
  // date pickers need them to restrict selectable days.
  const [projectDates, setProjectDates] = useState(null);
  useEffect(() => {
    setProjectDates(null);
    const id = selectedProject?._id;
    if (!id) return;
    projectService
      .getProjectById(id)
      .then((res) => {
        const p = res?.data;
        if (p) setProjectDates({ start: p.startDate, end: p.deadline });
      })
      .catch(() => {});
  }, [selectedProject?._id]);

  // ── Drag & drop: reorder cards within a list + reorder lists ──────
  // distance: 8 keeps plain clicks working (card opens task details) —
  // a drag only starts after the pointer moves 8px.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  // Set briefly after a drop so the browser's ghost click can't open
  // the task-details modal (consumed by SortableCard's onClickCapture).
  const justDraggedRef = useRef(false);

  const handleDragEnd = ({ active, over }) => {
    justDraggedRef.current = true;
    setTimeout(() => {
      justDraggedRef.current = false;
    }, 0);

    if (!over || active.id === over.id) return;
    const type = active.data?.current?.type;

    // Reorder whole lists (columns) — a personal preference saved locally
    // per account, permanent for this user, invisible to other accounts.
    if (type === "column") {
      const oldIdx = columns.findIndex((c) => c._id === active.id);
      const newIdx = columns.findIndex((c) => c._id === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      const newCols = arrayMove(columns, oldIdx, newIdx);
      updateColumns(newCols);
      saveBoardOrder(selectedProject?._id, newCols);
      return;
    }

    if (type !== "card") return;

    const sourceIdx = columns.findIndex((c) =>
      (c.tasks || []).some((t) => t._id === active.id),
    );
    if (sourceIdx === -1) return;

    // Resolve where the card was dropped: on another card (insert at its
    // position) or on a list itself (append — covers empty lists).
    const overType = over.data?.current?.type;
    let targetIdx;
    let insertAt;
    if (overType === "card") {
      targetIdx = columns.findIndex((c) =>
        (c.tasks || []).some((t) => t._id === over.id),
      );
      if (targetIdx === -1) return;
      insertAt = (columns[targetIdx].tasks || []).findIndex(
        (t) => t._id === over.id,
      );
    } else if (overType === "column") {
      targetIdx = columns.findIndex((c) => c._id === over.id);
      if (targetIdx === -1) return;
      insertAt = (columns[targetIdx].tasks || []).length;
    } else {
      return;
    }

    // Cards may only be reordered within their own list — dropping on a
    // different list is a no-op (the card snaps back to its original spot).
    if (targetIdx !== sourceIdx) return;

    const tasks = columns[sourceIdx].tasks || [];
    const oldIdx = tasks.findIndex((t) => t._id === active.id);
    if (oldIdx === -1 || insertAt === -1 || oldIdx === insertAt) return;
    const newCols = columns.map((c, i) =>
      i === sourceIdx ? { ...c, tasks: arrayMove(tasks, oldIdx, insertAt) } : c,
    );
    updateColumns(newCols);
    saveBoardOrder(selectedProject?._id, newCols);
  };

  // ── Re-apply this account's saved board order after every load ────
  // The backend returns the shared order on each fetch; this restores
  // the user's personal arrangement. The signature guard stops loops
  // (once applied, the order matches and nothing re-dispatches).
  useEffect(() => {
    if (!selectedProject?._id || columns.length === 0) return;
    const saved = loadBoardOrder(selectedProject._id);
    if (!saved) return;
    const ordered = orderBySaved(columns, saved.lists).map((c) => ({
      ...c,
      tasks: orderBySaved(c.tasks || [], saved.tasks?.[c._id]),
    }));
    if (boardOrderSignature(ordered) !== boardOrderSignature(columns)) {
      updateColumns(ordered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, selectedProject?._id]);

  // ── Keep outlet context columns in sync (for Projects.js) ────────
  useEffect(() => {
    if (setOutletColumns) setOutletColumns(columns);
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Close assignee dropdown on outside click ─────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(e.target)
      ) {
        setShowAssigneeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Local UI state (all unchanged) ───────────────────────────────
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [addTaskError, setAddTaskError] = useState("");
  const [showAddListModal, setShowAddListModal] = useState(false);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [showAssigneeDrop, setShowAssigneeDrop] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [activeColumnIndex, setActiveColumnIndex] = useState(null);

  const [taskData, setTaskData] = useState({
    name: "",
    taskType: "design",
    priority: "high",
    estimatedHours: "",
    description: "",
    taskStartDate: "",
    taskDeadline: "",
    assignees: [],
  });

  const [listData, setListData] = useState({
    listName: "",
    taskName: "",
    taskType: "design",
    priority: "high",
    taskStartDate: "",
    taskDeadline: "",
    estimatedHours: "",
    description: "",
  });

  const [activeMenu, setActiveMenu] = useState(null);
  const [showEditPriority, setShowEditPriority] = useState(false);
  const [showEditTaskType, setShowEditTaskType] = useState(false);
  const [showAddPriority, setShowAddPriority] = useState(false);
  const [showAddTaskType, setShowAddTaskType] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTaskColIndex, setEditingTaskColIndex] = useState(null);
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [editTaskData, setEditTaskData] = useState({
    name: "",
    taskType: "design",
    priority: "high",
    estimatedHours: "",
    taskStartDate: "",
    taskDeadline: "",
    assignees: [],
    description: "",
  });

  const [selectedTaskIndex, setSelectedTaskIndex] = useState(null);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentInput, setCommentInput] = useState("");
  const [commentTaskIndex, setCommentTaskIndex] = useState(null);
  const [commentColumnIndex, setCommentColumnIndex] = useState(null);
  const [showMentionBox, setShowMentionBox] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [newSubtask, setNewSubtask] = useState("");
  const [showSubtaskInput, setShowSubtaskInput] = useState(false);
  const newSubtaskRowRef = useRef(null);

  // Clicking outside the "new subtask" draft row discards it without
  // creating anything — only subtasks that were actually saved via Create
  // should ever appear in the list.
  useEffect(() => {
    if (!showSubtaskInput) return;
    const handleClickOutside = (e) => {
      if (
        newSubtaskRowRef.current &&
        !newSubtaskRowRef.current.contains(e.target)
      ) {
        setShowSubtaskInput(false);
        setNewSubtask("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSubtaskInput]);
  const [taskPendingDelete, setTaskPendingDelete] = useState(null); // { colIndex, taskIndex }
  // In-app replacement for window.alert() — every error/notice in this page
  // routes through here instead of the native browser dialog.
  const [errorPopup, setErrorPopup] = useState(""); // "" = closed
  const showError = (message) => setErrorPopup(message);
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  const [logsSearch, setLogsSearch] = useState("");
  const [logsDeleteStep, setLogsDeleteStep] = useState(0); // 0 = closed, 1 = "Delete Logs?", 2 = "Confirm Permanent Deletion?"
  const [logsDeleteConfirmText, setLogsDeleteConfirmText] = useState("");
  const [logsDeleteError, setLogsDeleteError] = useState("");

  // ── Load activity logs when the panel opens; reset all local
  //    delete/search state so a stale confirmation can't carry over ──
  useEffect(() => {
    if (showActivityLogs && selectedProject?._id) {
      loadProjectLogs(selectedProject._id, 1);
    }
    if (!showActivityLogs) {
      clearLogs();
      setLogsSearch("");
      setLogsDeleteStep(0);
      setLogsDeleteConfirmText("");
      setLogsDeleteError("");
    }
  }, [showActivityLogs, selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const commentInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const modalFileInputRef = useRef(null);
  // True while the OS file dialog is being opened from the comment composer —
  // the dialog blurs the textarea, and this flag stops that blur from
  // collapsing the composer.
  const attachingFileRef = useRef(false);
  const assigneeDropdownRef = useRef(null);
  // Comments scroll containers — the "Delete this comment?" confirmation
  // is centered within whichever of these panels is currently open.
  const taskDetailCommentsListRef = useRef(null);
  const commentsModalListRef = useRef(null);

  const selectedTask =
    selectedTaskIndex !== null && selectedColumnIndex !== null
      ? columns[selectedColumnIndex]?.tasks[selectedTaskIndex]
      : null;

  // ── ADD LIST ─────────────────────────────────────────────────────
  const handleAddList = () => setShowAddListModal(true);

  const handleCreateList = async () => {
    if (!listData.listName) {
      showError("Please enter a list name");
      return;
    }
    try {
      await addList(selectedProject._id, listData.listName).unwrap();
      setListData({
        listName: "",
        taskName: "",
        taskType: "design",
        priority: "high",
        taskStartDate: "",
        taskDeadline: "",
        estimatedHours: "",
        description: "",
      });
      setShowAddListModal(false);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to create list."));
    }
  };

  // ── ADD TASK ─────────────────────────────────────────────────────
  const handleAddTask = async () => {
    if (activeColumnIndex === null) return;

    // All Add Card fields are mandatory — errors show inside the card
    const err = (() => {
      if (!taskData.name?.trim()) return "*Task name is required.";
      if (!taskData.taskType) return "*Task type is required.";
      if (!taskData.priority) return "*Priority is required.";
      if (!taskData.taskStartDate) return "*Start date is required.";
      if (!taskData.taskDeadline) return "*End date is required.";
      if (
        projectStartDate &&
        dayValue(taskData.taskStartDate) < dayValue(projectStartDate)
      )
        return "*Start date cannot be before the project start date.";
      if (
        projectEndDate &&
        dayValue(taskData.taskDeadline) > dayValue(projectEndDate)
      )
        return "*End date cannot be after the project end date.";
      if (dayValue(taskData.taskDeadline) < dayValue(taskData.taskStartDate))
        return "*End date cannot be before the start date.";
      if (selectedAssignees.length === 0)
        return "*At least one task assignee is required.";
      if (!taskData.estimatedHours || parseInt(taskData.estimatedHours) <= 0)
        return "*Estimated hours is required.";
      if (taskData.taskStartDate && taskData.taskDeadline) {
        const maxHours = getMaxEstimatedHours(
          taskData.taskStartDate,
          taskData.taskDeadline,
        );
        if (parseInt(taskData.estimatedHours) > maxHours) {
          return `*Estimated hours cannot exceed ${maxHours} (max 8 hours/day for the selected dates).`;
        }
      }
      if (!taskData.description?.trim()) return "*Description is required.";
      return "";
    })();
    if (err) {
      setAddTaskError(err);
      return;
    }
    setAddTaskError("");

    const listId = columns[activeColumnIndex]._id;
    const payload = {
      name: taskData.name,
      taskType: taskData.taskType,
      priority: taskData.priority,
      estimatedHours: taskData.estimatedHours,
      taskStartDate: toDateOnlyISO(taskData.taskStartDate),
      taskDeadline: toDateOnlyISO(taskData.taskDeadline),
      description: taskData.description,
      assignees: selectedAssignees.map((u) => u._id),
    };
    try {
      await addTask(selectedProject._id, listId, payload).unwrap();
      setShowTaskModal(false);
      setShowAddPriority(false);
      setShowAddTaskType(false);
      setSelectedAssignees([]);
      setTaskData({
        name: "",
        taskType: "design",
        priority: "high",
        estimatedHours: "",
        description: "",
        taskStartDate: "",
        taskDeadline: "",
        assignees: [],
      });
    } catch (err) {
      setAddTaskError(getErrorMessage(err, "*Failed to add task."));
    }
  };

  // ── DELETE TASK ──────────────────────────────────────────────────
  const handleDeleteTask = async (colIndex, taskIndex) => {
    const listId = columns[colIndex]._id;
    const taskId = columns[colIndex].tasks[taskIndex]._id;
    try {
      await removeTask(selectedProject._id, listId, taskId).unwrap();
    } catch (err) {
      showError(getErrorMessage(err, "Failed to delete task."));
    }
  };

  // ── TOGGLE STATUS ────────────────────────────────────────────────
  const toggleTaskComplete = async () => {
    if (selectedTaskIndex === null || selectedColumnIndex === null) return;
    const task = columns[selectedColumnIndex].tasks[selectedTaskIndex];
    const listId = columns[selectedColumnIndex]._id;
    try {
      await toggleStatus(selectedProject._id, listId, task._id).unwrap();
    } catch (err) {
      showError(getErrorMessage(err, "Failed to update task status."));
    }
  };

  // ── POST COMMENT ─────────────────────────────────────────────────
  const handlePostComment = async () => {
    const taskIdx =
      commentTaskIndex !== null ? commentTaskIndex : selectedTaskIndex;
    const colIdx =
      commentColumnIndex !== null ? commentColumnIndex : selectedColumnIndex;
    if (!commentInput.trim() && uploadedFiles.length === 0) {
      return;
    }

    const column = colIdx !== null ? columns[colIdx] : null;
    const task = column?.tasks?.[taskIdx];
    if (!column || !task) return;
    const listId = column._id;

    let payload;
    if (uploadedFiles.length > 0) {
      const formData = new FormData();
      formData.append("text", commentInput.trim());
      if (mentionedUserIds.length > 0) {
        formData.append("mentionedUserIds", JSON.stringify(mentionedUserIds));
      }
      uploadedFiles.forEach((f) => formData.append("files", f.file));
      payload = formData;
    } else {
      payload = { text: commentInput.trim(), mentionedUserIds };
    }

    try {
      await sendComment(selectedProject._id, listId, task._id, payload);

      setCommentInput("");
      setUploadedFiles([]);
      setShowMentionBox(false);
      setMentionedUserIds([]);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to post comment."));
    }
  };

  const [activeCommentMenu, setActiveCommentMenu] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [commentPendingDelete, setCommentPendingDelete] = useState(null);

  // task/listId are passed explicitly so this works from both the Comments
  // Modal (commentColumnIndex) and the Task Details comment section
  // (selectedColumnIndex) — the two comment lists don't share a task ref.
  const handleDeleteComment = async (comment, task, listId) => {
    if (!task || !listId) return;

    try {
      await deleteComment(selectedProject._id, listId, task._id, comment._id);
      await loadBoard(selectedProject._id);
    } catch (err) {
      showError(err?.response?.data?.message || "Unable to delete comment.");
    }
  };

  const handleEditComment = (comment) => {
    setEditingCommentId(comment._id);
    setEditingCommentText(comment.text);
    setActiveCommentMenu(null);
  };

  // ── ADD SUBTASK ──────────────────────────────────────────────────
  const handleAddSubtask = async () => {
    if (
      !newSubtask.trim() ||
      selectedTaskIndex === null ||
      selectedColumnIndex === null
    )
      return;

    const task = columns[selectedColumnIndex].tasks[selectedTaskIndex];
    const listId = columns[selectedColumnIndex]._id;

    try {
      await addSubtask(
        selectedProject._id,
        listId,
        task._id,
        newSubtask.trim(),
      ).unwrap();

      await loadBoard(selectedProject._id);

      // Refresh selected task reference
      const updatedColumn = columns[selectedColumnIndex];
      const updatedTask = updatedColumn?.tasks[selectedTaskIndex];

      if (updatedTask) {
        setSelectedTaskIndex(selectedTaskIndex);
      }

      setNewSubtask("");
      setShowSubtaskInput(false);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to add subtask."));
    }
  };

  const user = getLoggedInUser();
  const userRole = user?.role?.toLowerCase();
  // Managers have the same board permissions as admins.
  const canManageBoard = userRole === "admin" || userRole === "manager";

  // A subtask's Edit/Delete icons are only shown to whoever created it, or
  // to an admin/manager (who can act on any subtask) — mirrors the
  // backend's own "isOwn || admin/manager" rule on those routes.
  const canEditSubtask = (subtask) => {
    if (canManageBoard) return true;
    const creatorId = subtask?.createdBy?._id || subtask?.createdBy || null;
    return Boolean(creatorId) && String(creatorId) === String(user?._id);
  };

  // Only the employees assigned to this project, normalized to flat user
  // objects. Members can arrive as { user: {...} } or already flat.
  const projectMembers = (members || []).map((m) =>
    m?.user && typeof m.user === "object" ? m.user : m,
  );

  // Project date bounds (validated) — task dates must stay inside them.
  // selectedProject can come from sources that omit the date fields, so
  // fall back to the same project in the projects list.
  const toValidDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };
  const projectMeta = (projects || []).find(
    (p) => p._id === selectedProject?._id,
  );
  const projectStartDate =
    toValidDate(projectDates?.start) ||
    toValidDate(selectedProject?.startDate) ||
    toValidDate(projectMeta?.startDate);
  const projectEndDate =
    toValidDate(projectDates?.end) ||
    toValidDate(selectedProject?.deadline) ||
    toValidDate(projectMeta?.deadline);
  // Compare by calendar day, ignoring time-of-day
  const dayValue = (v) => {
    const d = new Date(v);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };

  const handleDeleteSubtask = async (subtaskId) => {
    if (selectedTaskIndex === null || selectedColumnIndex === null) return;

    const task = columns[selectedColumnIndex]?.tasks?.[selectedTaskIndex];
    const listId = columns[selectedColumnIndex]?._id;
    if (!task || !listId) return;

    try {
      await deleteSubtask(selectedProject._id, listId, task._id, subtaskId);

      await loadBoard(selectedProject._id);
    } catch (err) {
      alert(getErrorMessage(err, "Failed to delete subtask."));
    }
  };

  const handleSaveSubtask = async (subtaskId, title) => {
    if (selectedTaskIndex === null || selectedColumnIndex === null) return;

    const task = columns[selectedColumnIndex]?.tasks?.[selectedTaskIndex];
    const listId = columns[selectedColumnIndex]?._id;
    if (!task || !listId) return;

    try {
      await updateSubtask(
        selectedProject._id,
        listId,
        task._id,
        editingSubtaskId,
        editingSubtaskText,
      );

      await loadBoard(selectedProject._id);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to update subtask."));
    } finally {
      // Always leave edit mode — on failure (e.g. a permission error) the
      // row must not stay stuck editable with no way out but a refresh.
      setEditingSubtaskId(null);
      setEditingSubtaskText("");
    }
  };

  // ── TOGGLE SUBTASK ───────────────────────────────────────────────
  const handleToggleSubtask = async (subtaskId) => {
    if (selectedTaskIndex === null || selectedColumnIndex === null) return;

    const task = columns[selectedColumnIndex].tasks[selectedTaskIndex];
    const listId = columns[selectedColumnIndex]._id;

    try {
      await toggleSubtask(
        selectedProject._id,
        listId,
        task._id,
        subtaskId,
      ).unwrap();

      await loadBoard(selectedProject._id);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to update subtask."));
    }
  };
  // ── EDIT TASK ────────────────────────────────────────────────────
  const handleEditTask = (colIdx, taskIdx) => {
    const task = columns[colIdx].tasks[taskIdx];
    setEditingTaskColIndex(colIdx);
    setEditingTaskIndex(taskIdx);
    setEditTaskData({
      name: task.name || "",
      taskType: task.taskType || "design",
      priority: task.priority || "high",
      estimatedHours: task.estimatedHours || "",
      taskStartDate: task.taskStartDate || "",
      taskDeadline: task.taskDeadline || "",
      assignees: task.assignees || [],
      description: task.description || "",
    });
    // Resolve against projectMembers, not allUsers — a task can carry an
    // assignee who has since been removed from the project (removing a
    // member doesn't touch their existing task assignments). Silently
    // keeping such a person selected would let the form submit an invalid
    // assignee and fail server-side with "Task assignees must be members
    // of the project." Dropping them here means the field just shows one
    // fewer assignee, and saving no longer fails for this reason.
    const resolvedAssignees = (task.assignees || [])
      .map((a) => {
        const id = typeof a === "string" ? a : a._id;
        return projectMembers.find((u) => u._id === id) || null;
      })
      .filter(Boolean);
    setSelectedAssignees(resolvedAssignees);
    setShowEditTaskModal(true);
    setActiveMenu(null);
  };

  const handleSaveEditTask = async () => {
    if (
      !editTaskData.name ||
      editingTaskColIndex === null ||
      editingTaskIndex === null
    ) {
      showError("Please enter a task name");
      return;
    }
    if (editTaskData.taskStartDate && editTaskData.taskDeadline) {
      const maxHours = getMaxEstimatedHours(
        editTaskData.taskStartDate,
        editTaskData.taskDeadline,
      );
      if (parseInt(editTaskData.estimatedHours) > maxHours) {
        showError(
          `Estimated hours cannot exceed ${maxHours} (max 8 hours/day for the selected dates).`,
        );
        return;
      }
    }
    const listId = columns[editingTaskColIndex]._id;
    const taskId = columns[editingTaskColIndex].tasks[editingTaskIndex]._id;
    const payload = {
      ...editTaskData,
      taskStartDate: toDateOnlyISO(editTaskData.taskStartDate),
      taskDeadline: toDateOnlyISO(editTaskData.taskDeadline),
      assignees: selectedAssignees.map((u) => u._id),
    };
    try {
      // updateTask() just dispatches the thunk — a plain dispatch() never
      // rejects even if the request fails, so without .unwrap() a failed
      // save would fall through to the success path below (modal closes,
      // no error shown, but nothing was actually persisted).
      await updateTask(selectedProject._id, listId, taskId, payload).unwrap();
      setShowEditTaskModal(false);
      setEditingTaskColIndex(null);
      setEditingTaskIndex(null);
      setSelectedAssignees([]);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to update task."));
    }
  };

  // ── @MENTION handlers (unchanged) ────────────────────────────────
  const handleCommentChange = (e) => {
    const value = e.target.value;
    setCommentInput(value);
    const lastAtIndex = value.lastIndexOf("@");
    if (lastAtIndex !== -1) {
      const afterAt = value.substring(lastAtIndex + 1);
      if (afterAt.includes(" ")) {
        setShowMentionBox(false);
      } else if (!afterAt.includes("\n")) {
        setMentionSearch(afterAt);
        setShowMentionBox(true);
      }
    } else {
      setShowMentionBox(false);
      setMentionSearch("");
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = commentInput.lastIndexOf("@");
    const before = commentInput.substring(0, lastAtIndex);
    const after = commentInput.substring(
      lastAtIndex + 1 + mentionSearch.length,
    );
    setCommentInput(`${before}@${user.name} ${after}`);
    setShowMentionBox(false);
    setMentionSearch("");
    setMentionedUserIds((prev) =>
      prev.includes(user._id) ? prev : [...prev, user._id],
    );
    setTimeout(() => commentInputRef.current?.focus(), 0);
  };

  const getMentionFilteredUsers = () => {
    // Only members assigned to this project (normalized flat user objects)
    const users = projectMembers;

    if (!mentionSearch) return users;

    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(mentionSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(mentionSearch.toLowerCase()),
    );
  };

  // ── FILE handlers ─────────────────────────────────────────────────
  // Mirrors the backend's allowed list exactly (upload.service.js):
  // images (jpg, jpeg, png, gif, webp) + documents (pdf, doc, docx, xls,
  // xlsx, txt, zip).
  const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "application/zip",
    "application/x-zip-compressed",
  ];
  const ALLOWED_FILE_EXT = /\.(png|jpe?g|gif|webp|pdf|docx?|xlsx?|txt|zip)$/i;

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);

    const accepted = [];
    const rejected = [];
    files.forEach((file) => {
      const okType =
        ALLOWED_FILE_TYPES.includes(file.type) ||
        ALLOWED_FILE_EXT.test(file.name);
      if (okType) accepted.push(file);
      else rejected.push(file.name);
    });

    if (rejected.length > 0) {
      showError(
        `These files are not allowed (only images, PDF, Word, Excel, TXT, or ZIP):\n${rejected.join(
          "\n",
        )}`,
      );
    }

    const newFiles = accepted.map((file) => ({
      id: `${file.name}-${Date.now()}`,
      name: file.name,
      size: file.size,
      file,
      type: file.type,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (modalFileInputRef.current) modalFileInputRef.current.value = "";
  };

  const removeUploadedFile = (fileId) =>
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));

  const getFileIcon = (fileType) =>
    fileType?.startsWith("image/") ? "🖼️" : "📎";

  const getCommentTask = () => {
    if (commentTaskIndex !== null && commentColumnIndex !== null)
      return columns[commentColumnIndex]?.tasks[commentTaskIndex];
    return null;
  };

  const [isCommentFocused, setIsCommentFocused] = useState(false);
  const [newComment, setNewComment] = useState("");

  const handleAddComment = async () => {
    if (!newComment.trim() && uploadedFiles.length === 0) {
      return;
    }
    if (
      !selectedProject?._id ||
      selectedColumnIndex === null ||
      !columns[selectedColumnIndex]?._id ||
      !selectedTask?._id
    )
      return;

    try {
      await sendComment(
        selectedProject._id,
        columns[selectedColumnIndex]._id,
        selectedTask._id,
        uploadedFiles.length
          ? (() => {
              const formData = new FormData();

              formData.append("text", newComment);

              formData.append(
                "mentionedUserIds",
                JSON.stringify(mentionedUserIds),
              );

              uploadedFiles.forEach((f) => formData.append("files", f.file));

              return formData;
            })()
          : {
              text: newComment,
              mentionedUserIds,
            },
      );

      await loadBoard(selectedProject._id);

      setNewComment("");
      setUploadedFiles([]);
      setMentionedUserIds([]);
      setShowTaskMentionBox(false);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to post comment."));
    }
  };

  // ── CARD HELPERS (unchanged) ──────────────────────────────────────
  const getCardColor = (task) => {
    if (task.status === "done") return "bg-green-100 border border-green-400";
    const deadline = task.taskDeadline || task.endDate;
    if (deadline) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const end = new Date(deadline);
      end.setHours(0, 0, 0, 0);
      if (end < today) return "bg-red-100 border border-red-400";
    }
    return "bg-white";
  };

  // Assignee stack on a task card.
  // - Opens on HOVER (not click) and lists every assignee, not just the two
  //   shown in the stack.
  // - The panel is portalled to <body> and positioned from the stack's viewport
  //   rect: the card itself is `overflow-hidden` (fixed 226px height), so an
  //   absolutely-positioned child would be clipped and never appear.
  // - `ml-auto` pushes the stack to the card's right edge.
  const renderAssignees = (assignees = [], popupKey) => {
    const visible = assignees.slice(0, 2);
    const extra = assignees.length - visible.length;
    const isOpen = openAssigneePopup === popupKey;

    const openFromEvent = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setAssigneePopupRect(rect);
      setOpenAssigneePopup(popupKey);
    };

    return (
      <div className="flex items-center ml-auto relative">
        <div
          className="flex items-center -space-x-2 cursor-pointer"
          onMouseEnter={openFromEvent}
          onMouseLeave={() => {
            setOpenAssigneePopup(null);
            setAssigneePopupRect(null);
          }}
          // Keyboard/touch parity — hover alone is not reachable on either.
          onFocus={openFromEvent}
          onBlur={() => {
            setOpenAssigneePopup(null);
            setAssigneePopupRect(null);
          }}
          tabIndex={0}
          onClick={(e) => e.stopPropagation()} // don't open task details
        >
          {visible.map((u) => {
            const img = getProfileImageUrl(u);
            return (
              <div
                key={u._id}
                className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center overflow-hidden text-xs font-semibold bg-[#F3F4F6] text-[#7F7F7F]"
                title={u.name}
              >
                {img ? (
                  <img
                    src={img}
                    alt={u.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  getInitials(u.name)
                )}
              </div>
            );
          })}
          {extra > 0 && (
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs text-gray-500 font-medium border-2 border-white">
              +{extra}
            </div>
          )}
        </div>

        {isOpen &&
          assignees.length > 0 &&
          assigneePopupRect &&
          createPortal(
            <div
              style={getAssigneePopupStyle(assigneePopupRect, assignees.length)}
              className="w-[220px] bg-white border border-[#CFCFCF] rounded-[8px] px-4 py-3 shadow-lg z-[9999] max-h-[240px] overflow-y-auto"
            >
              {assignees.map((a) => {
                const img = getProfileImageUrl(a);
                return (
                  <div key={a._id} className="flex items-center gap-3 py-1.5">
                    <div className="w-[32px] h-[32px] rounded-full flex items-center justify-center overflow-hidden text-[12px] font-semibold bg-[#F3F4F6] text-[#7F7F7F] shrink-0">
                      {img ? (
                        <img
                          src={img}
                          alt={a.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        getInitials(a.name)
                      )}
                    </div>
                    <p className="text-[12px] leading-[22px] text-[#4A4A4A] truncate">
                      {a.name}
                    </p>
                  </div>
                );
              })}
            </div>,
            document.body,
          )}
      </div>
    );
  };

  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);

  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskText, setEditingSubtaskText] = useState("");
  const editingSubtaskRowRef = useRef(null);

  const handleEditSubtask = (subtask) => {
    setEditingSubtaskId(subtask._id);
    setEditingSubtaskText(subtask.title || subtask.name);
  };

  // Clicking anywhere outside the subtask row currently being edited exits
  // edit mode without saving — same effect as the (removed) Cancel button.
  useEffect(() => {
    if (!editingSubtaskId) return;
    const handleClickOutside = (e) => {
      if (
        editingSubtaskRowRef.current &&
        !editingSubtaskRowRef.current.contains(e.target)
      ) {
        setEditingSubtaskId(null);
        setEditingSubtaskText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editingSubtaskId]);

  // ── SUBTASK ATTACHMENTS ────────────────────────────────────────────
  // Attachments live on the subtask itself (subtask.attachments, populated
  // by the backend) — no separate local cache. Any upload/delete just
  // re-fetches the board so the real, persisted data flows back in.
  const [openSubtaskAttachments, setOpenSubtaskAttachments] = useState(null);
  const [uploadingSubtaskId, setUploadingSubtaskId] = useState(null);
  const subtaskFileInputRef = useRef(null);
  const attachingSubtaskId = useRef(null);

  const MAX_SUBTASK_FILE_SIZE = 10 * 1024 * 1024; // 10 MB — matches backend limit

  const handleSubtaskFileUpload = async (e, subtaskId) => {
    const picked = Array.from(e.target.files || []);
    if (subtaskFileInputRef.current) subtaskFileInputRef.current.value = "";
    if (picked.length === 0) return;

    const files = [];
    const rejected = [];
    const oversized = [];
    picked.forEach((file) => {
      const okType =
        ALLOWED_FILE_TYPES.includes(file.type) ||
        ALLOWED_FILE_EXT.test(file.name);
      if (!okType) {
        rejected.push(file.name);
        return;
      }
      if (file.size > MAX_SUBTASK_FILE_SIZE) {
        oversized.push(file.name);
        return;
      }
      files.push(file);
    });

    if (rejected.length > 0) {
      showError(
        `These files are not allowed (only images, PDF, Word, Excel, TXT, or ZIP):\n${rejected.join(
          "\n",
        )}`,
      );
    }
    if (oversized.length > 0) {
      showError(
        `These files exceed the 10 MB limit and were not attached:\n${oversized.join(
          "\n",
        )}`,
      );
    }
    if (files.length === 0) return;

    if (files.length > 5) {
      showError("You can attach at most 5 files at a time.");
      return;
    }

    if (selectedTaskIndex === null || selectedColumnIndex === null) return;
    const task = columns[selectedColumnIndex]?.tasks?.[selectedTaskIndex];
    const listId = columns[selectedColumnIndex]?._id;
    if (!task || !listId) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    setUploadingSubtaskId(subtaskId);
    try {
      await addSubtaskAttachment(
        selectedProject._id,
        listId,
        task._id,
        subtaskId,
        formData,
      );
      await loadBoard(selectedProject._id);
      setOpenSubtaskAttachments(subtaskId);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to upload attachment."));
    } finally {
      setUploadingSubtaskId(null);
    }
  };

  const removeSubtaskAttachment = async (subtaskId, attachmentId) => {
    if (selectedTaskIndex === null || selectedColumnIndex === null) return;
    const task = columns[selectedColumnIndex]?.tasks?.[selectedTaskIndex];
    const listId = columns[selectedColumnIndex]?._id;
    if (!task || !listId) return;

    try {
      await deleteSubtaskAttachment(
        selectedProject._id,
        listId,
        task._id,
        subtaskId,
        attachmentId,
      );
      await loadBoard(selectedProject._id);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to delete attachment."));
    }
  };

  // ── COMMENT DELETE CONFIRMATION ─────────────────────────────────────
  // Centered over the comments panel it belongs to (not just below the
  // "..." button), rendered through a portal so it isn't clipped by the
  // panel's own overflow-y-auto. No screen-covering backdrop — only a
  // click-outside-the-popup listener closes it — so the comments list
  // stays scrollable and the rest of the page stays fully interactive
  // while it's open.
  const CommentDeletePopup = ({ containerRef, onCancel, onConfirm }) => {
    const popupRef = useRef(null);
    const [rect, setRect] = useState(null);

    useEffect(() => {
      const el = containerRef.current;
      if (el) setRect(el.getBoundingClientRect());
    }, [containerRef]);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (popupRef.current && !popupRef.current.contains(e.target)) {
          onCancel();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onCancel]);

    if (!rect) return null;

    return createPortal(
      <div
        ref={popupRef}
        style={{
          position: "fixed",
          top: rect.top + rect.height / 2,
          left: rect.left + rect.width / 2,
          transform: "translate(-50%, -50%)",
        }}
        className="bg-white border rounded-lg shadow-xl z-[9999] w-[220px] p-3"
      >
        <p className="text-sm text-gray-700 mb-3">Delete this comment?</p>
        <div className="flex justify-end gap-2">
          <button
            className="px-3 py-1 border rounded-md text-xs hover:bg-gray-50 transition"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1 bg-red-500 text-white rounded-md text-xs hover:bg-red-600 transition"
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>,
      document.body,
    );
  };

  // ── ASSIGNEE PICKER component ───────────────────────────────────────
  // Styled to match the Project Members dropdown in the Create Project
  // modal (Layout.js): search icon inside a bordered input, a floating
  // portal-rendered panel (never clipped by the Edit Task modal's
  // scroll area), circular profile-icon avatars, and a checkbox tick.
  const AssigneePicker = ({
    selected,
    onSelect,
    onRemove,
    className = "h-[40px]",
  }) => {
    // All members assigned to this project.
    const filtered = projectMembers.filter((u) =>
      u.name?.toLowerCase().includes(assigneeSearch.toLowerCase()),
    );
    const { refs, floatingStyles } = useFloating({
      open: showAssigneeDrop,
      onOpenChange: setShowAssigneeDrop,
      placement: "bottom-start",
      whileElementsMounted: autoUpdate,
      middleware: [
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          },
          padding: 8,
        }),
      ],
    });

    return (
      <div className="relative">
        <div className="relative mt-1" ref={refs.setReference}>
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">
            <Icons.Search className="w-4 h-4" />
          </div>
          <input
            className={`w-full border border-gray-300 rounded-[8px] pl-9 pr-3 text-[14px] ${className}`}
            placeholder="Search Members"
            value={assigneeSearch}
            onChange={(e) => {
              setAssigneeSearch(e.target.value);
              setShowAssigneeDrop(true);
            }}
            onFocus={() => setShowAssigneeDrop(true)}
          />
        </div>
        {showAssigneeDrop && (
          <FloatingPortal>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setShowAssigneeDrop(false)}
            />
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className="z-[9999] bg-white border border-gray-200 rounded-[8px] shadow-lg overflow-hidden"
            >
              <div className="max-h-[220px] overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="px-3 py-3 text-gray-400 text-sm">
                    No members found
                  </div>
                ) : (
                  filtered.map((u) => {
                    const isSelected = selected.some((s) => s._id === u._id);
                    return (
                      <div
                        key={u._id}
                        className="px-3 py-3 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0"
                        onClick={() => {
                          if (isSelected) {
                            onRemove(u._id);
                          } else {
                            onSelect(u);
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-[24px] h-[24px] rounded-full border border-gray-400 flex items-center justify-center flex-shrink-0">
                            <Icons.Profile />
                          </div>
                          <p className="text-[14px] font-medium text-black">
                            {u.name}
                          </p>
                        </div>
                        <div
                          className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? "bg-blue-500 border-blue-500"
                              : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </FloatingPortal>
        )}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {selected.map((u) => (
              <span
                key={u._id}
                className="flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full"
              >
                {u.name}
                <button
                  type="button"
                  onClick={() => onRemove(u._id)}
                  className="text-blue-400 hover:text-red-500 font-bold"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // LIST VIEW — shown when no project is selected
  // ═══════════════════════════════════════════════════════════════════

  if (!selectedProject) {
    return (
      <div className="px-4 md:px-6 py-5">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-[24px] md:text-[32px] font-semibold text-black">
            Kanban Board
          </h1>

          <p className="text-[14px] text-[#7E7E7E] mt-1">
            Organize Tasks, Track Progress, And Collaborate With Your Team In
            Real Time.
          </p>
        </div>

        {/* Project Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {projects.map((project) => (
            <div
              key={project._id}
              className="
              bg-white
              rounded-[16px]
              min-h-[237px]
              px-5
              py-5
              shadow-sm
            "
            >
              {/* Header */}
              <div className="flex gap-4">
                {/* Icon Box */}
                <div
                  className="
                  w-[52px]
                  h-[52px]
                  rounded-[16px]
                  bg-[#E7EEFF]
                  flex
                  items-center
                  justify-center
                "
                >
                  <Icons.KanbanPic />
                </div>

                {/* Project Name & Status */}
                <div>
                  <h2 className="text-[18px] md:text-[22px] font-medium">
                    Project {project.name}
                  </h2>

                  <span
                    className="
                    mt-2
                    inline-flex
                    px-3
                    h-[24px]
                    items-center
                    rounded-full
                    bg-[#D8F5D9]
                    text-[#1D7A31]
                    text-[11px]
                    font-medium
                  "
                  >
                    {project.progress ?? 0}% COMPLETED
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="border-b mt-4 mb-4"></div>

              {/* Statistics */}
              <div className="flex flex-col sm:flex-row gap-4 sm:justify-between">
                {/* Lists */}

                <div className="flex gap-2">
                  <div
                    className="
                    w-[48px]
                    h-[48px]
                    bg-[#EDF3FF]
                    rounded-[8px]
                    flex
                    items-center
                    justify-center
                  "
                  >
                    <Icons.KanbanClip />
                  </div>

                  <div>
                    <p className="text-[14px] font-medium">
                      {project.lists?.length || 0}
                    </p>

                    <p className="text-[10px] text-gray-400">lists</p>
                  </div>
                </div>

                {/* Members */}
                <div className="flex gap-2">
                  <div
                    className="
                    w-[48px]
                    h-[48px]
                    bg-[#F3ECFF]
                    rounded-[8px]
                    flex
                    items-center
                    justify-center
                  "
                  >
                    <Icons.KanbanTeam />
                  </div>

                  <div>
                    <p className="text-[14px] font-medium">
                      {project.members?.length || 0}
                    </p>

                    <p className="text-[10px] text-gray-400">team members</p>
                  </div>
                </div>

                {/* Tasks */}
                <div className="flex gap-2">
                  <div
                    className="
                    w-[48px]
                    h-[48px]
                    bg-[#FFF3E6]
                    rounded-[8px]
                    flex
                    items-center
                    justify-center
                  "
                  >
                    <Icons.KanbanTask />
                  </div>

                  <div>
                    <p className="text-[14px] font-medium">
                      {getTaskCount(project)}
                    </p>

                    <p className="text-[10px] text-gray-400">tasks</p>
                  </div>
                </div>
              </div>

              {/* Button */}
              <div className="flex justify-end mt-5">
                <button
                  onClick={() => {
                    selectProject(project);
                    navigate(`/dashboard/kanban/${project._id}`);
                  }}
                  className="
                  w-[126px]
                  h-[35px]
                  bg-[#4253C7]
                  rounded-[8px]
                  text-white
                  text-[13px]
                "
                >
                  View Details →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── ACTIVITY LOGS: pagination + two-step delete ──────────────────
  const handleLogsPageChange = (page) => {
    if (
      !selectedProject?._id ||
      page < 1 ||
      (logsPagination && page > logsPagination.totalPages)
    )
      return;
    loadProjectLogs(selectedProject._id, page);
  };

  const filteredLogs = (logs || []).filter((log) => {
    if (!logsSearch.trim()) return true;
    const q = logsSearch.trim().toLowerCase();
    return (
      log.action?.toLowerCase().includes(q) ||
      log.actor?.name?.toLowerCase().includes(q) ||
      log.targetUser?.name?.toLowerCase().includes(q)
    );
  });

  const handleConfirmPermanentDeletion = async () => {
    setLogsDeleteError("");
    const expectedName = (
      logsProject?.name ||
      selectedProject?.name ||
      ""
    ).trim();
    if (logsDeleteConfirmText.trim() !== expectedName) {
      setLogsDeleteError(
        "The project name must match exactly, including capitalization and spacing.",
      );
      return;
    }
    try {
      await removeProjectLogs(selectedProject._id);
      setLogsDeleteStep(0);
      setLogsDeleteConfirmText("");
    } catch (err) {
      setLogsDeleteError(getErrorMessage(err, "Failed to delete logs."));
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // BOARD VIEW
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className="p-6" onClick={() => setActiveMenu(null)}>
      <button
        className="text-blue-500 hover:text-blue-700 text-[16px] w-[39px] h-[24px] mb-3"
        onClick={() => {
          clearBoard();
          navigate("/dashboard/kanban");
        }}
      >
        Back
      </button>

      <div className="bg-white p-4 rounded-xl flex justify-between items-center mb-6 w-full h-[84px]">
        <h2 className="text-lg font-semibold">
          Project {selectedProject.name}
        </h2>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            className="border border-[#4253C7] text-[#4253C7] w-full sm:w-[163px] h-[40px] rounded-lg flex items-center justify-center gap-2"
            onClick={() => setShowActivityLogs(true)}
          >
            <Icons.Logs />
            View Logs
          </button>
          {canManageBoard && (
            <button
              className="bg-[#4253C7] text-white w-full sm:w-[163px] h-[40px] rounded-lg"
              onClick={handleAddList}
            >
              Add List
            </button>
          )}
        </div>
      </div>

      {columns.length === 0 && (
        <div className="text-center text-gray-400 mt-20">
          No lists yet. Click "Add List" to start 🚀
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <div
          className="flex gap-2 overflow-x-auto overflow-y-hidden pb-4 hide-scrollbar cursor-grab active:cursor-grabbing"
          onMouseDown={(e) => {
            const el = e.currentTarget;
            let startX = e.pageX - el.offsetLeft;
            let scrollLeft = el.scrollLeft;
            let isDragging = false;

            const onMouseMove = (e) => {
              const x = e.pageX - el.offsetLeft;
              const walk = x - startX;

              if (Math.abs(walk) > 5) {
                e.preventDefault();
                isDragging = true;
                el.scrollLeft = scrollLeft - walk;
              }
            };

            const onMouseUp = (e) => {
              if (isDragging) e.preventDefault();

              window.removeEventListener("mousemove", onMouseMove);
              window.removeEventListener("mouseup", onMouseUp);
            };

            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
          }}
        >
          <SortableContext
            items={columns.map((col) => col._id)}
            strategy={horizontalListSortingStrategy}
          >
            {columns.map((col, i) => (
              <SortableList key={col._id} id={col._id}>
                {({ dragHandleProps }) => (
                  <div
                    className="
          p-2 sm:p-4
          w-[85vw]
          sm:w-[292px]
          flex-shrink-0
        "
                  >
                    <div className="bg-[#CEDEFB] rounded-[8px] p-[24px] w-full sm:w-[292px]">
                      <h3
                        className="font-md text-[20px] mb-4 cursor-grab active:cursor-grabbing select-none"
                        {...dragHandleProps}
                      >
                        {col.name}
                      </h3>
                      <SortableContext
                        items={(col.tasks || []).map(
                          (t, idx) => t._id || `card-${i}-${idx}`,
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        {(col.tasks || []).map((task, idx) => (
                          <SortableCard
                            key={task._id || idx}
                            id={task._id || `card-${i}-${idx}`}
                            clickGuardRef={justDraggedRef}
                          >
                            {/* Design size 244×226 — 244 is exactly the
                                column's 292px inner width minus its 24px
                                padding, so w-full hits it on desktop and
                                shrinks with the column on narrow screens.
                                Height is fixed (not min-h) so every card is
                                identical regardless of content: long titles
                                clamp, and the optional deadline/subtask rows
                                hold reserved space instead of collapsing.
                                overflow-hidden guarantees nothing can push
                                the card taller than its neighbours. */}
                            <div
                              data-task-card
                              className={`${getCardColor(task)} p-[24px] rounded-lg mb-4 shadow-sm w-full max-w-[244px] h-[226px] overflow-hidden mx-auto relative`}
                              onClick={() => {
                                setSelectedTaskIndex(idx);
                                setSelectedColumnIndex(i);
                                setCommentTaskIndex(idx);
                                setCommentColumnIndex(i);
                                setShowTaskDetails(true);
                                // Task details renders the comment thread, so
                                // this counts as seeing them too.
                                markCommentsSeen(task);
                              }}
                            >
                              {/* CONTENT - LEFT SIDE */}
                              <div className="pr-16">
                                {/* Title clamps to one line so a long task
                                    name cannot grow the fixed-height card. */}
                                <p className="text-[16px] font-medium flex items-center gap-2 min-w-0">
                                  {task?.status === "done" && (
                                    <span className="shrink-0">
                                      <Icons.Check />
                                    </span>
                                  )}
                                  <span className="truncate">{task?.name}</span>
                                </p>
                                <p className="text-[14px] text-gray-400 mt-1 truncate">
                                  {task.taskType || task.type}
                                </p>
                                <div className="mt-2">
                                  <span
                                    className={`inline-block px-2 py-1 text-xs rounded-full ${getPriorityBadge(task.priority)}`}
                                  >
                                    {formatPriority(task.priority)}
                                  </span>
                                </div>
                                {/* Fixed-height row: keeps the layout below it
                                    identical whether or not a deadline is set. */}
                                <div className="mt-3 h-6">
                                  {(task.taskDeadline || task.endDate) && (
                                    <p className="text-[16px] text-gray-500 truncate">
                                      {new Date(
                                        task.taskDeadline || task.endDate,
                                      ).toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                      })}
                                    </p>
                                  )}
                                </div>
                                <div className="flex pt-[4px]">
                                  {/* Both rows keep a fixed height and the
                                      subtask row always occupies its slot, so
                                      cards with and without subtasks align. */}
                                  <div className="flex flex-col gap-2 min-w-0">
                                    <p className="text-[16px] text-gray-500 h-6 truncate">
                                      {task.estimatedHours || task.hours} Hours
                                    </p>

                                    <div className="text-gray-500 font-medium text-[16px] h-6 truncate">
                                      {task.subtasks &&
                                        task.subtasks.length > 0 && (
                                          <>
                                            {
                                              task.subtasks.filter(
                                                (st) => st.isCompleted,
                                              ).length
                                            }
                                            /{task.subtasks.length} subtasks
                                          </>
                                        )}
                                    </div>
                                  </div>

                                  {/* -mr-16 cancels the parent's pr-16 (which
                                      exists to clear the top-right icons) so
                                      the avatar stack reaches the card's true
                                      right edge. */}
                                  <div className="flex items-center text-[16px] text-gray-500 ml-auto -mr-16 relative">
                                    {renderAssignees(
                                      task.assignees || [],
                                      `${i}-${idx}`,
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* ICONS - TOP RIGHT */}
                              <div className="absolute top-3 right-3 flex gap-2 p-[16px] items-center">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Measure the task card (not the icon) so
                                    // the panel can align to the card's edges.
                                    const card =
                                      e.currentTarget.closest(
                                        "[data-task-card]",
                                      );
                                    setCommentAnchorRect(
                                      card
                                        ? card.getBoundingClientRect()
                                        : e.currentTarget.getBoundingClientRect(),
                                    );
                                    setCommentTaskIndex(idx);
                                    setCommentColumnIndex(i);
                                    setShowComments(true);
                                    markCommentsSeen(task);
                                  }}
                                  className="cursor-pointer relative"
                                >
                                  <Icons.Notify className="text-blue-500 hover:text-blue-700" />
                                  {/* Unseen-comment indicator — presence only,
                                      no count, per design. */}
                                  {hasUnseenComments(
                                    task,
                                    currentUserId,
                                    commentsSeen,
                                  ) && (
                                    <span
                                      aria-label="Unread comments"
                                      className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white pointer-events-none"
                                      style={{ backgroundColor: "#FF0000" }}
                                    />
                                  )}
                                </button>
                                <div className="relative">
                                  {canManageBoard && (
                                    <div className="relative">
                                      <MoreHorizontal
                                        size={18}
                                        className="cursor-pointer hover:text-gray-700"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveMenu(
                                            activeMenu === `${i}-${idx}`
                                              ? null
                                              : `${i}-${idx}`,
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {activeMenu === `${i}-${idx}` && (
                                    <div className="absolute right-0 mt-2 w-32 bg-white shadow-lg rounded-md z-50 p-2">
                                      <button
                                        className="block w-full text-left text-sm px-2 py-1 hover:bg-gray-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditTask(i, idx);
                                          setShowEditTaskModal(true);
                                          setActiveMenu(null);
                                        }}
                                      >
                                        Edit Task
                                      </button>
                                      <button
                                        className="block w-full text-left text-sm px-2 py-1 text-red-500 hover:bg-gray-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTaskPendingDelete({
                                            colIndex: i,
                                            taskIndex: idx,
                                          });
                                          setActiveMenu(null);
                                        }}
                                      >
                                        Delete Task
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SortableCard>
                        ))}
                      </SortableContext>
                      {canManageBoard && (
                        <p
                          className="text-[#4253C7] text-[16px] font-medium cursor-pointer underline"
                          onClick={() => {
                            setActiveColumnIndex(i);
                            setSelectedAssignees([]);
                            setAddTaskError("");
                            setShowTaskModal(true);
                          }}
                        >
                          Add Card
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </SortableList>
            ))}
          </SortableContext>
        </div>
      </DndContext>

      {/* ADD CARD MODAL */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white w-[95vw] md:w-[700px] p-6 rounded-xl relative max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Add Task</h2>
            <p className="text-gray-600 mb-6">
              Define tasks for your Project Workflow
            </p>
            <button
              className="absolute top-[24px] right-[24px]"
              onClick={() => {
                setShowTaskModal(false);
                setShowAddPriority(false);
                setShowAddTaskType(false);
              }}
            >
              ✕
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* LEFT COLUMN */}
              <div className="space-y-4">
                {/* Task Name */}
                <div>
                  <label className="text-sm font-medium">Task name</label>
                  <input
                    className="w-full h-[40px] mt-1 border p-2 rounded-md"
                    placeholder="Enter task name"
                    value={taskData.name}
                    onChange={(e) =>
                      setTaskData({ ...taskData, name: e.target.value })
                    }
                  />
                </div>

                {/* Priority */}
                <div className="relative">
                  <label className="text-sm font-medium">Priority</label>
                  <div
                    className="w-full h-[40px] mt-1 border rounded-md px-3 flex items-center justify-between cursor-pointer"
                    onClick={() => setShowAddPriority((prev) => !prev)}
                  >
                    <span className="text-sm capitalize text-gray-700">
                      {taskData.priority}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${
                        showAddPriority ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {showAddPriority && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowAddPriority(false)}
                      />
                      <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-[220px] overflow-y-auto">
                        {["critical", "high", "medium", "low"].map((option) => (
                          <div
                            key={option}
                            className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 cursor-pointer capitalize"
                            onClick={() => {
                              setTaskData({ ...taskData, priority: option });
                              setShowAddPriority(false);
                            }}
                          >
                            <span className="text-sm">{option}</span>
                            {taskData.priority === option && (
                              <span className="text-green-600">✓</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Start Date */}
                <div>
                  <label className="text-sm font-medium">Start Date</label>

                  <div className="relative mt-1">
                    {/* Calendar Icon */}
                    <div className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 z-10">
                      <Icons.Calendar />
                    </div>

                    <DatePicker
                      wrapperClassName="w-full"
                      selected={
                        taskData.taskStartDate
                          ? new Date(taskData.taskStartDate)
                          : null
                      }
                      onChange={(date) =>
                        setTaskData({
                          ...taskData,
                          taskStartDate: date ? date.toISOString() : "",
                        })
                      }
                      placeholderText="Select start date"
                      className="w-full h-[40px] border p-0 pl-[55px] pr-3 rounded-md text-[14px] outline-none"
                      dateFormat="dd MMM yyyy"
                      minDate={projectStartDate}
                      maxDate={projectEndDate}
                    />
                  </div>
                </div>

                {/* Task Assignee */}
                <div className="relative" ref={assigneeDropdownRef}>
                  <label className="text-sm font-medium">Task Assignee</label>

                  <div
                    className="w-full h-[40px] mt-1 border rounded-md px-3 flex items-center justify-between cursor-pointer"
                    onClick={() => setShowAssigneeDropdown((prev) => !prev)}
                  >
                    <span className="text-sm text-gray-500">
                      {selectedAssignees.length > 0
                        ? `${selectedAssignees.length} employee(s) selected`
                        : "Search Members"}
                    </span>

                    <ChevronDown
                      size={16}
                      className={`transition-transform ${
                        showAssigneeDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {showAssigneeDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-50 overflow-hidden">
                      <div className="max-h-[220px] overflow-y-auto">
                        {projectMembers.length === 0 ? (
                          <div className="px-3 py-3 text-[14px] text-gray-400">
                            No members found
                          </div>
                        ) : (
                          projectMembers.map((member) => {
                            const selected = selectedAssignees.some(
                              (a) => a._id === member._id,
                            );
                            return (
                              <div
                                key={member._id}
                                className="px-3 py-3 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0"
                                onClick={() => {
                                  if (selected) {
                                    setSelectedAssignees((prev) =>
                                      prev.filter((a) => a._id !== member._id),
                                    );
                                  } else {
                                    setSelectedAssignees((prev) => [
                                      ...prev,
                                      member,
                                    ]);
                                  }
                                }}
                              >
                                {/* LEFT */}
                                <div className="flex items-center gap-3">
                                  <div className="w-[24px] h-[24px] rounded-full border border-gray-400 flex items-center justify-center">
                                    <Icons.Profile />
                                  </div>

                                  <div>
                                    <p className="text-[14px] font-medium text-black">
                                      {member.name}
                                    </p>
                                  </div>
                                </div>

                                {/* RIGHT TICK */}
                                <div
                                  className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center ${
                                    selected
                                      ? "bg-blue-500 border-blue-500"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {selected && (
                                    <svg
                                      width="10"
                                      height="10"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="white"
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-4">
                {/* Task Type */}
                <div className="relative">
                  <label className="text-sm font-medium">Task Type</label>
                  <div
                    className="w-full h-[40px] mt-1 border rounded-md px-3 flex items-center justify-between cursor-pointer"
                    onClick={() => setShowAddTaskType((prev) => !prev)}
                  >
                    <span className="text-sm capitalize text-gray-700">
                      {taskData.taskType}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${
                        showAddTaskType ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {showAddTaskType && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowAddTaskType(false)}
                      />
                      <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-[220px] overflow-y-auto">
                        {[
                          "requirements",
                          "design",
                          "frontend",
                          "backend",
                          "testing",
                          "deployment",
                          "others",
                        ].map((option) => (
                          <div
                            key={option}
                            className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 cursor-pointer capitalize"
                            onClick={() => {
                              setTaskData({ ...taskData, taskType: option });
                              setShowAddTaskType(false);
                            }}
                          >
                            <span className="text-sm">{option}</span>
                            {taskData.taskType === option && (
                              <span className="text-green-600">✓</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Estimated Hours */}
                <div>
                  <label className="text-sm font-medium">Estimated Hours</label>
                  <input
                    type="number"
                    min={1}
                    max={getMaxEstimatedHours(
                      taskData.taskStartDate,
                      taskData.taskDeadline,
                    )}
                    className="w-full h-[40px] mt-1 border p-2 rounded-md"
                    placeholder="e.g., 8"
                    value={taskData.estimatedHours}
                    onChange={(e) => {
                      const maxHours = getMaxEstimatedHours(
                        taskData.taskStartDate,
                        taskData.taskDeadline,
                      );
                      const raw = e.target.value;
                      const capped =
                        raw !== "" && Number(raw) > maxHours
                          ? String(maxHours)
                          : raw;
                      setTaskData({
                        ...taskData,
                        estimatedHours: capped,
                      });
                    }}
                  />
                  {taskData.taskStartDate && taskData.taskDeadline && (
                    <p className="text-xs text-gray-400 mt-1">
                      Max{" "}
                      {getMaxEstimatedHours(
                        taskData.taskStartDate,
                        taskData.taskDeadline,
                      )}{" "}
                      hours (8 hours/day) for the selected dates.
                    </p>
                  )}
                </div>

                {/* End Date */}
                <div>
                  <label className="text-sm font-medium">End Date</label>

                  <div className="relative mt-1">
                    {/* Calendar Icon */}
                    <div className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 z-10">
                      <Icons.Calendar />
                    </div>

                    <DatePicker
                      wrapperClassName="w-full"
                      selected={
                        taskData.taskDeadline
                          ? new Date(taskData.taskDeadline)
                          : null
                      }
                      onChange={(date) =>
                        setTaskData({
                          ...taskData,
                          taskDeadline: date ? date.toISOString() : "",
                        })
                      }
                      placeholderText="Select end date"
                      className="w-full h-[40px] border p-0 pl-[55px] pr-1 rounded-md text-[14px] outline-none"
                      dateFormat="dd MMM yyyy"
                      minDate={
                        taskData.taskStartDate
                          ? new Date(taskData.taskStartDate)
                          : projectStartDate
                      }
                      maxDate={projectEndDate}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="w-full mt-2 border p-3 rounded-md resize-none"
                rows="3"
                placeholder="Add description..."
                value={taskData.description || ""}
                onChange={(e) =>
                  setTaskData({ ...taskData, description: e.target.value })
                }
              />
            </div>
            {addTaskError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                {addTaskError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
                onClick={() => {
                  setShowTaskModal(false);
                  setShowAddPriority(false);
                  setShowAddTaskType(false);
                  setAddTaskError("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-[#4253C7] text-white rounded-md hover:bg-blue-600"
                onClick={handleAddTask}
                disabled={isCreatingTask}
              >
                {isCreatingTask ? "Creating..." : "Create Task"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TASK DETAILS MODAL */}

      {showTaskDetails && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white w-[95vw] md:w-[600px] p-6 rounded-xl relative max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-[24px] right-[24px] text-gray-500 hover:text-gray-700"
              onClick={() => {
                setShowTaskDetails(false);
                setSelectedTaskIndex(null);
                setSelectedColumnIndex(null);
                setCommentTaskIndex(null);
                setCommentColumnIndex(null);
                setMentionedUserIds([]);
                // Discard any unsent comment draft — comments should only
                // be saved when Send is clicked, not by closing the panel.
                setNewComment("");
                setUploadedFiles([]);
                setIsCommentFocused(false);
                setShowTaskMentionBox(false);
                setTaskMentionSearch("");
                // Discard any unsaved subtask draft — a subtask should only
                // be saved when Create is clicked, not by closing the panel.
                setNewSubtask("");
                setShowSubtaskInput(false);
                setEditingSubtaskId(null);
                setEditingSubtaskText("");
                setOpenSubtaskAttachments(null);
              }}
            >
              ✕
            </button>
            <div className="mb-4 flex flex-wrap items-center gap-3 border-b pb-3">
              {/* Task Type */}
              <span className="text-sm bg-gray-200 px-2 py-1 rounded">
                {selectedTask.taskType || selectedTask.type}
              </span>

              {/* Status Toggle */}
              <span
                className={`text-2xl ${
                  userRole !== "viewer" ? "cursor-pointer" : ""
                }`}
                onClick={() => {
                  if (userRole !== "viewer") {
                    toggleTaskComplete();
                  }
                }}
              >
                {selectedTask?.status === "done" ? <Icons.Check /> : "○"}
              </span>

              {/* Task Name */}
              <p className="text-lg font-semibold ">{selectedTask?.name}</p>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold mb-2">Description</h3>
              <textarea
                rows="3"
                className="border p-2 flex-1 rounded-md w-full resize-none"
                value={selectedTask?.description || ""}
                readOnly
              />
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Subtasks</h3>
                {selectedTask?.subtasks?.length > 0 && (
                  <span className="text-xs text-gray-400">
                    {selectedTask.subtasks.filter((s) => s.isCompleted).length}/
                    {selectedTask.subtasks.length} Subtasks
                  </span>
                )}
              </div>
              <div className="border rounded-[8px] p-2 space-y-2">
                {!selectedTask?.subtasks ||
                selectedTask.subtasks.length === 0 ? (
                  !showSubtaskInput ? (
                    <div className="border border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center w-full h-[209px] gap-4">
                      <Icons.Subtask className="w-[133px] h-[68px]" />
                      {userRole !== "viewer" && (
                        <button
                          className="w-[221px] h-[40px] bg-white border border-[#4253C7] text-[#4253C7] rounded-md font-medium hover:bg-blue-50 transition"
                          onClick={() => {
                            setNewSubtask("");
                            setShowSubtaskInput((p) => !p);
                          }}
                        >
                          +Add your first Subitem
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      ref={newSubtaskRowRef}
                      className="w-full flex items-center gap-2"
                    >
                      <input
                        type="text"
                        className="flex-1 h-[40px] border border-[#4253C7] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4253C7]"
                        placeholder="Enter subtask name..."
                        value={newSubtask}
                        onChange={(e) => setNewSubtask(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setShowSubtaskInput(false);
                            setNewSubtask("");
                          }
                        }}
                        autoFocus
                      />
                      <button
                        type="button"
                        disabled={!newSubtask.trim()}
                        className="h-[40px] px-4 bg-[#4253C7] text-white rounded-md text-sm font-medium disabled:opacity-50"
                        onClick={() => {
                          if (!newSubtask.trim()) return;
                          handleAddSubtask();
                          setShowSubtaskInput(false);
                        }}
                      >
                        Create
                      </button>
                    </div>
                  )
                ) : (
                  <div
                    className={`space-y-2 ${
                      selectedTask.subtasks.length > 5
                        ? "max-h-[232px] overflow-y-auto pr-1"
                        : ""
                    }`}
                  >
                    {selectedTask.subtasks.map((subtask) => (
                      <div
                        key={subtask._id}
                        ref={
                          editingSubtaskId === subtask._id
                            ? editingSubtaskRowRef
                            : null
                        }
                        // While editing, the row drops its own border/padding
                        // so the inner input+Create pair reads exactly like
                        // the create-subtask row (which has no such wrapper).
                        // The 1px transparent border keeps the total height
                        // identical to view mode (24 text + 16 p-2 + 2 border
                        // = 42px; 40px input + 2px border = 42px), so entering
                        // edit mode does not shift the list.
                        className={`flex items-center gap-2 rounded-md border ${
                          editingSubtaskId === subtask._id
                            ? "border-transparent"
                            : "border-gray-300 p-2"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={subtask.isCompleted}
                          disabled={userRole === "viewer"}
                          onChange={() => handleToggleSubtask(subtask._id)}
                          className="w-[15px] h-[15px] cursor-pointer flex-shrink-0 accent-[#216DFA]"
                        />

                        {editingSubtaskId === subtask._id ? (
                          // Same styling as the create-subtask input below, so
                          // adding and editing a subtask look identical.
                          <input
                            value={editingSubtaskText}
                            onChange={(e) =>
                              setEditingSubtaskText(e.target.value)
                            }
                            className="flex-1 min-w-0 h-[40px] border border-[#4253C7] rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4253C7]"
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                await handleSaveSubtask(
                                  subtask._id,
                                  editingSubtaskText,
                                );
                              } else if (e.key === "Escape") {
                                setEditingSubtaskId(null);
                                setEditingSubtaskText("");
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <span className="flex-1">
                            {subtask.title || subtask.name}
                          </span>
                        )}

                        {/* Attachments — the paperclip icon opens the file
                            picker (to add more files); the count text opens
                            the panel showing what's already attached.
                            Hidden while editing so only Create shows. */}
                        {userRole !== "viewer" &&
                          editingSubtaskId !== subtask._id &&
                          (() => {
                            const hasAttachments =
                              (subtask.attachments?.length || 0) > 0;
                            return (
                              <div
                                className={`flex items-center gap-1 text-xs rounded-md ${
                                  hasAttachments
                                    ? "border border-dashed border-[#4253C7] text-[#4253C7] px-2 py-1"
                                    : "text-[#000000]"
                                }`}
                              >
                                <button
                                  type="button"
                                  title="Attach a file"
                                  disabled={uploadingSubtaskId === subtask._id}
                                  onClick={() => {
                                    attachingSubtaskId.current = subtask._id;
                                    subtaskFileInputRef.current?.click();
                                  }}
                                  className={`flex items-center disabled:opacity-50 ${
                                    hasAttachments
                                      ? "text-[#4253C7]"
                                      : "hover:text-gray-600"
                                  }`}
                                >
                                  <Icons.Attachment
                                    className="w-4 h-4"
                                    opacity={hasAttachments ? 1 : 0.56}
                                  />
                                </button>
                                {uploadingSubtaskId === subtask._id && (
                                  <span className="text-gray-400">
                                    Uploading...
                                  </span>
                                )}
                                {hasAttachments && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setOpenSubtaskAttachments((prev) =>
                                        prev === subtask._id
                                          ? null
                                          : subtask._id,
                                      )
                                    }
                                    className="hover:underline whitespace-nowrap"
                                  >
                                    {subtask.attachments.length} attachment
                                    {subtask.attachments.length > 1 ? "s" : ""}
                                  </button>
                                )}
                              </div>
                            );
                          })()}

                        {/* Edit/Delete icons — only the subtask's own
                            creator, or an admin/manager, can act on it.
                            Both hide while this row is being edited so the
                            Create button is the only action offered. */}
                        {canEditSubtask(subtask) &&
                          editingSubtaskId !== subtask._id && (
                            <button
                              onClick={() => handleEditSubtask(subtask)}
                              className="flex items-center text-blue-500 hover:text-blue-700"
                            >
                              <Icons.BPencil className="w-4 h-4" />
                            </button>
                          )}

                        {canEditSubtask(subtask) &&
                          editingSubtaskId !== subtask._id && (
                            <button
                              onClick={() => handleDeleteSubtask(subtask._id)}
                              className="flex items-center text-red-500 hover:text-red-700"
                            >
                              <Icons.Delete className="w-4 h-4" />
                            </button>
                          )}

                        {/* Create — saves the subtask name being edited.
                            No Cancel button: clicking outside the row (see
                            the click-outside handler below) or pressing
                            Escape exits edit mode instead. */}
                        {userRole !== "viewer" &&
                          editingSubtaskId === subtask._id && (
                            <button
                              type="button"
                              // Matches the create-subtask Create button.
                              className="h-[40px] px-4 bg-[#4253C7] text-white rounded-md text-sm font-medium disabled:opacity-50 flex-shrink-0"
                              disabled={!editingSubtaskText.trim()}
                              onClick={() =>
                                handleSaveSubtask(
                                  subtask._id,
                                  editingSubtaskText,
                                )
                              }
                            >
                              Create
                            </button>
                          )}
                      </div>
                    ))}
                    <div className="flex justify-start mt-2">
                      {!showSubtaskInput ? (
                        userRole !== "viewer" && (
                          <button
                            className="text-blue-500 hover:text-blue-700 text-[14px] font-medium cursor-pointer transition"
                            onClick={() => {
                              setNewSubtask("");
                              setShowSubtaskInput((p) => !p);
                            }}
                          >
                            +Add Subitem
                          </button>
                        )
                      ) : (
                        <div
                          ref={newSubtaskRowRef}
                          className="w-full flex items-center gap-2"
                        >
                          <input
                            type="text"
                            className="flex-1 h-[40px] border border-[#4253C7] rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4253C7]"
                            placeholder="Enter subtask name..."
                            value={newSubtask}
                            onChange={(e) => setNewSubtask(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") {
                                setShowSubtaskInput(false);
                                setNewSubtask("");
                              }
                            }}
                            autoFocus
                          />
                          <button
                            type="button"
                            disabled={!newSubtask.trim()}
                            className="h-[40px] px-4 bg-[#4253C7] text-white rounded-md text-sm font-medium disabled:opacity-50"
                            onClick={() => {
                              if (!newSubtask.trim()) return;
                              handleAddSubtask();
                              setShowSubtaskInput(false);
                            }}
                          >
                            Create
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Attachments panel for whichever subtask's paperclip was clicked —
                  rendered outside the Subtasks border box, not inside it. */}
              {openSubtaskAttachments &&
                (() => {
                  const openSubtask = selectedTask?.subtasks?.find(
                    (s) => s._id === openSubtaskAttachments,
                  );
                  const attachments = openSubtask?.attachments || [];
                  if (attachments.length === 0) return null;

                  return (
                    <div className="mt-3 border border-gray-200 rounded-2xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-lg text-gray-900">
                          Attachments
                        </h4>
                        <button
                          type="button"
                          onClick={() => setOpenSubtaskAttachments(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {attachments.map((attachment) => {
                          const canDelete =
                            userRole === "admin" ||
                            attachment.uploadedBy?._id === user?._id ||
                            attachment.uploadedBy === user?._id;
                          const safeUrl = getSafeUrl(attachment.url);
                          // uploadedBy is normally populated ({ _id, name,
                          // email }) by the backend. If it ever comes
                          // through as a bare ID instead (stale cache,
                          // un-populated response), resolve the name from
                          // known users rather than showing "Unknown".
                          const uploaderId =
                            attachment.uploadedBy?._id || attachment.uploadedBy;
                          const uploaderName =
                            attachment.uploadedBy?.name ||
                            (uploaderId === user?._id ? user?.name : null) ||
                            projectMembers.find((m) => m._id === uploaderId)
                              ?.name ||
                            (allUsers || []).find((u) => u._id === uploaderId)
                              ?.name ||
                            "Unknown";
                          const isPdf = /\.pdf$/i.test(
                            attachment.fileName || "",
                          );
                          return (
                            <div
                              key={attachment._id}
                              className="w-full max-w-[134px] min-h-[128px] border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col"
                            >
                              <div className="relative flex-1 min-h-[72px] bg-gray-50 flex items-center justify-center">
                                <a
                                  href={safeUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-full h-full flex items-center justify-center"
                                >
                                  {attachment.fileType === "image" &&
                                  safeUrl ? (
                                    <img
                                      src={safeUrl}
                                      alt={attachment.fileName}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : isPdf ? (
                                    <span className="flex flex-col items-center justify-center w-9 h-11 bg-[#E02424] rounded-sm text-white">
                                      <FileText className="w-4 h-4" />
                                      <span className="text-[7px] font-bold leading-none mt-0.5">
                                        PDF
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="text-2xl">
                                      {getFileIcon(attachment.fileType)}
                                    </span>
                                  )}
                                </a>
                                {canDelete && (
                                  <button
                                    type="button"
                                    title="Remove attachment"
                                    onClick={() =>
                                      removeSubtaskAttachment(
                                        openSubtaskAttachments,
                                        attachment._id,
                                      )
                                    }
                                    className="absolute top-1 right-1 bg-white shadow rounded-full w-4 h-4 flex items-center justify-center text-gray-500 hover:text-red-500"
                                  >
                                    <X className="w-2.5 h-2.5" />
                                  </button>
                                )}
                              </div>
                              <div className="px-1.5 py-1">
                                <p
                                  className="text-[10px] font-medium text-gray-900 truncate"
                                  title={attachment.fileName}
                                >
                                  {attachment.fileName}
                                </p>
                                <p className="text-[9px] text-gray-400 truncate">
                                  {formatFileSize(attachment.fileSize)}
                                </p>
                                <p
                                  className="text-[9px] text-gray-400 truncate"
                                  title={`Uploaded by ${uploaderName}`}
                                >
                                  Uploaded by {uploaderName}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

              <input
                ref={subtaskFileInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,image/png,image/jpeg,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip,application/x-zip-compressed"
                className="hidden"
                onChange={(e) =>
                  handleSubtaskFileUpload(e, attachingSubtaskId.current)
                }
              />
            </div>
            <h3 className="font-semibold mb-2">Comments</h3>
            <div
              ref={taskDetailCommentsListRef}
              className="space-y-2 mb-4 max-h-[150px] overflow-y-auto"
            >
              {selectedTask?.comments && selectedTask.comments.length > 0 ? (
                selectedTask.comments.map((comment, ci) => (
                  <div
                    key={comment._id || ci}
                    className="relative bg-gray-50 p-3 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <span className="font-semibold text-sm">
                        {comment.author?.name || comment.author || "User"}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(comment.createdAt) ||
                            comment.timestamp}
                        </span>
                        <div className="relative flex-shrink-0">
                          <button
                            className="text-gray-500 px-1 hover:text-gray-700"
                            onClick={() =>
                              setActiveCommentMenu(
                                activeCommentMenu === comment._id
                                  ? null
                                  : comment._id,
                              )
                            }
                          >
                            <MoreHorizontal size={16} />
                          </button>

                          {activeCommentMenu === comment._id && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setActiveCommentMenu(null)}
                              />
                              <div className="absolute right-full top-0 mr-1 bg-white border rounded-md shadow-lg z-50 w-[100px]">
                                <button
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                  onClick={() => {
                                    handleEditComment(comment);
                                    setActiveCommentMenu(null);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-gray-100"
                                  onClick={() => {
                                    setCommentPendingDelete({
                                      comment,
                                      task: selectedTask,
                                      listId: columns[selectedColumnIndex]?._id,
                                    });
                                    setActiveCommentMenu(null);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}

                          {commentPendingDelete?.comment._id ===
                            comment._id && (
                            <CommentDeletePopup
                              containerRef={taskDetailCommentsListRef}
                              onCancel={() => setCommentPendingDelete(null)}
                              onConfirm={() => {
                                handleDeleteComment(
                                  commentPendingDelete.comment,
                                  commentPendingDelete.task,
                                  commentPendingDelete.listId,
                                );
                                setCommentPendingDelete(null);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {editingCommentId === comment._id ? (
                      <div className="mt-2">
                        <textarea
                          value={editingCommentText}
                          onChange={(e) =>
                            setEditingCommentText(e.target.value)
                          }
                          className="w-full border rounded-md p-2 text-sm resize-none"
                          rows={3}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            className="px-3 py-1 border rounded-md text-sm"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditingCommentText("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="px-3 py-1 bg-[#4253C7] text-white rounded-md text-sm"
                            onClick={async () => {
                              const listId = columns[selectedColumnIndex]?._id;
                              const text = editingCommentText.trim();
                              if (!text || !listId || !selectedTask) return;

                              try {
                                await updateComment(
                                  selectedProject._id,
                                  listId,
                                  selectedTask._id,
                                  comment._id,
                                  { text },
                                );
                                await loadBoard(selectedProject._id);
                                setEditingCommentId(null);
                                setEditingCommentText("");
                              } catch (err) {
                                showError(
                                  err?.response?.data?.message ||
                                    "Unable to update comment.",
                                );
                              }
                            }}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 mt-1">
                        {comment.text}
                      </p>
                    )}
                    {comment.attachments && comment.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {comment.attachments.map((attachment) => (
                          <a
                            key={attachment._id}
                            href={getSafeUrl(attachment.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-1 text-xs hover:bg-blue-100 transition"
                          >
                            <span>{getFileIcon(attachment.fileType)}</span>
                            <span className="truncate max-w-[100px]">
                              {attachment.fileName}
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No comments yet
                </p>
              )}
            </div>
            <div className="flex gap-2 items-end">
              <div className="w-full mt-4">
                {!isCommentFocused ? (
                  <input
                    type="text"
                    placeholder="Write a comment..."
                    className="w-full h-[42px] border border-gray-300 rounded-lg px-3 text-sm outline-none"
                    onFocus={() => setIsCommentFocused(true)}
                  />
                ) : (
                  <div className="relative border border-gray-300 rounded-lg">
                    {showTaskMentionBox && (
                      <div className="absolute bottom-full left-0 mb-2 w-64 bg-white border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                        {getTaskMentionUsers().length > 0 ? (
                          getTaskMentionUsers().map((user) => (
                            <button
                              key={user._id}
                              type="button"
                              onMouseDown={() => insertTaskMention(user)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100"
                            >
                              {user.name}
                            </button>
                          ))
                        ) : (
                          <p className="p-3 text-sm text-gray-500">
                            No project members found
                          </p>
                        )}
                      </div>
                    )}
                    <textarea
                      rows={3}
                      autoFocus
                      value={newComment}
                      onChange={handleTaskCommentChange}
                      placeholder="Write a comment..."
                      className="w-full p-3 resize-none outline-none text-sm"
                      onBlur={() => {
                        // Ignore the blur caused by opening the file dialog
                        if (attachingFileRef.current) {
                          attachingFileRef.current = false;
                          return;
                        }
                        // Keep the composer open while files are staged
                        if (!newComment.trim() && uploadedFiles.length === 0) {
                          setIsCommentFocused(false);
                        }
                      }}
                    />

                    {uploadedFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2 px-3 pb-2">
                        {uploadedFiles.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs hover:bg-blue-100 transition group"
                          >
                            <span>{getFileIcon(file.type)}</span>
                            <span className="truncate max-w-[120px] font-medium">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeUploadedFile(file.id)}
                              className="ml-1 text-gray-400 hover:text-red-500 transition font-bold"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between border-t px-3 py-2">
                      {/* Left Actions */}
                      <div className="flex items-center gap-3">
                        {/* File Upload */}
                        <>
                          <input
                            ref={modalFileInputRef}
                            type="file"
                            multiple
                            accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,image/png,image/jpeg,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip,application/x-zip-compressed"
                            className="hidden"
                            onChange={handleFileUpload}
                          />

                          <button
                            type="button"
                            className="text-gray-500 hover:text-blue-500"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              attachingFileRef.current = true;
                              modalFileInputRef.current?.click();
                            }}
                          >
                            <Paperclip size={16} />
                          </button>
                        </>

                        {/* Mention */}
                        <button
                          type="button"
                          className="text-gray-500 hover:text-blue-500"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setNewComment((prev) => prev + "@");

                            if (projectMembers.length > 0) {
                              setShowTaskMentionBox(true);
                            }

                            setTaskMentionSearch("");
                          }}
                        >
                          @
                        </button>
                      </div>

                      {/* Right Action */}
                      <button
                        type="button"
                        className="px-4 py-1 bg-[#4253C7] text-white rounded-md hover:bg-blue-600"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleAddComment}
                      >
                        Send
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* COMMENTS MODAL */}
      {showCommentModal && getCommentTask() && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white w-[95vw] md:w-[500px] p-6 rounded-xl relative max-h-[600px] overflow-y-auto">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={() => {
                setShowCommentModal(false);
                setCommentTaskIndex(null);
                setCommentColumnIndex(null);
                setCommentInput("");
                setUploadedFiles([]);
                setShowMentionBox(false);
                setMentionedUserIds([]);
              }}
            >
              ✕
            </button>
            <h2 className="text-lg font-semibold mb-4">Comments</h2>
            <p className="text-sm text-gray-600 mb-4 pb-4 border-b">
              Task:{" "}
              <span className="font-medium">{getCommentTask()?.name}</span>
            </p>
            <div className="space-y-4 mb-6 min-h-[150px]">
              {getCommentTask()?.comments &&
              getCommentTask().comments.length > 0 ? (
                getCommentTask().comments.map((comment, ci) => (
                  <div
                    key={comment._id || ci}
                    className="bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <span className="font-semibold text-sm">
                        {comment.author?.name || comment.author || "User"}
                      </span>

                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(comment.createdAt) ||
                          comment.timestamp}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-2">{comment.text}</p>
                    {comment.attachments && comment.attachments.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {comment.attachments.map((attachment) => (
                          <a
                            key={attachment._id}
                            href={getSafeUrl(attachment.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs hover:bg-blue-100 transition"
                          >
                            <span>{getFileIcon(attachment.fileType)}</span>
                            <span className="truncate max-w-[150px]">
                              {attachment.fileName}
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">
                  No comments yet. Be the first to comment!
                </p>
              )}
            </div>
            <div className="border-t pt-4">
              <div className="flex items-end gap-3 relative">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  <Icons.U />{" "}
                </div>
                <div className="flex-1">
                  {showMentionBox && (
                    <div className="absolute bottom-full left-12 mb-2 w-56 bg-white border border-gray-300 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                      {getMentionFilteredUsers().length > 0 ? (
                        <div className="py-1">
                          {getMentionFilteredUsers().map((user) => (
                            <button
                              key={user._id}
                              type="button"
                              onClick={() => insertMention(user)}
                              className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center gap-2 transition text-sm"
                            >
                              <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                <Icons.U />{" "}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {user.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {user.email}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="px-4 py-3 text-sm text-gray-500 text-center">
                          No users found
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex items-center border rounded-xl focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition">
                    <textarea
                      placeholder="Write your comment... (use @mention for teammates)"
                      ref={commentInputRef}
                      rows={2}
                      value={commentInput}
                      onChange={handleCommentChange}
                      className="flex-1 border rounded-md px-3 py-2 outline-none resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handlePostComment();
                        }
                      }}
                    />
                  </div>
                  {uploadedFiles.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs hover:bg-blue-100 transition group"
                        >
                          <span>{getFileIcon(file.type)}</span>
                          <span className="truncate max-w-[120px] font-medium">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeUploadedFile(file.id)}
                            className="ml-1 text-gray-400 hover:text-red-500 transition font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,image/png,image/jpeg,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip,application/x-zip-compressed"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition"
                      title="Attach files"
                    >
                      <Paperclip size={18} />
                    </button>
                    <button
                      type="button"
                      className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition"
                      title="Mention teammate"
                      onClick={() => {
                        setCommentInput((prev) => {
                          const updated = prev + "@";
                          setMentionSearch("");
                          return updated;
                        });

                        setShowMentionBox(true);

                        setTimeout(() => {
                          commentInputRef.current?.focus();
                        }, 0);
                      }}
                    >
                      <AtSign size={18} />
                    </button>
                    <button
                      className="ml-auto p-2 text-[#4253C7] hover:bg-blue-100 rounded-lg transition"
                      onClick={handlePostComment}
                      disabled={
                        !commentInput.trim() && uploadedFiles.length === 0
                      }
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD LIST MODAL */}
      {showAddListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white w-[90vw] md:w-[400px] p-6 rounded-xl relative">
            <button
              className="absolute top-4 right-4"
              onClick={() => setShowAddListModal(false)}
            >
              ✕
            </button>
            <h2 className="text-lg font-semibold mb-2">Add List</h2>
            <p className="text-sm text-gray-600 mb-6">
              Enter a name for the new list
            </p>
            <div>
              <label className="text-sm font-medium">List name</label>
              <input
                className="w-full mt-1 border p-2 rounded-md"
                placeholder="e.g., In Progress"
                value={listData.listName}
                onChange={(e) =>
                  setListData({ ...listData, listName: e.target.value })
                }
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
                onClick={() => setShowAddListModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-[#4253C7] text-white rounded-md hover:bg-blue-600"
                onClick={handleCreateList}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT TASK MODAL */}
      {showEditTaskModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          onClick={() => setActiveMenu(null)}
        >
          <div
            className="bg-white w-[95vw] md:w-[700px] p-6 rounded-xl relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={() => {
                setShowEditTaskModal(false);
                setShowEditPriority(false);
                setShowEditTaskType(false);
                setEditingTaskColIndex(null);
                setEditingTaskIndex(null);
                setSelectedAssignees([]);
              }}
            >
              ✕
            </button>
            <h2 className="text-lg font-semibold mb-2">Edit Task</h2>
            <p className="text-sm text-gray-600 mb-6">Update task details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* LEFT COLUMN */}
              <div className="space-y-4">
                {/* Task Name */}
                <div>
                  <label className="text-sm font-medium">Task name</label>
                  <input
                    className="w-full h-[40px] mt-1 border p-2 rounded-md"
                    value={editTaskData.name}
                    onChange={(e) =>
                      setEditTaskData({
                        ...editTaskData,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="text-sm font-medium">Priority</label>
                  <div className="relative w-full mt-1">
                    {/* Trigger */}
                    <div
                      className="w-full h-[40px] border border-gray-300 rounded-[8px] px-3 flex items-center justify-between cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                      onClick={() => setShowEditPriority((prev) => !prev)}
                    >
                      <span className="capitalize text-[14px] text-gray-800">
                        {editTaskData.priority}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`text-gray-500 transition-transform ${
                          showEditPriority ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    {showEditPriority && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowEditPriority(false)}
                        />
                        <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-300 rounded-[8px] shadow-lg z-50 overflow-hidden">
                          {["critical", "high", "medium", "low"].map((item) => (
                            <div
                              key={item}
                              className="px-3 py-3 hover:bg-blue-500 hover:text-white cursor-pointer capitalize text-[14px] transition-colors border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                setEditTaskData({
                                  ...editTaskData,
                                  priority: item,
                                });
                                setShowEditPriority(false);
                              }}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <label className="text-sm font-medium">Start Date</label>

                  <div className="relative mt-1">
                    <div className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 z-10">
                      <Icons.Calendar />
                    </div>

                    <DatePicker
                      wrapperClassName="w-full"
                      selected={
                        editTaskData.taskStartDate
                          ? new Date(editTaskData.taskStartDate)
                          : null
                      }
                      onChange={(date) =>
                        setEditTaskData({
                          ...editTaskData,
                          taskStartDate: date ? date.toISOString() : "",
                        })
                      }
                      className="w-full h-[40px] border p-0 pl-[55px] pr-3 rounded-md text-[14px] outline-none"
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select start date"
                    />
                  </div>
                </div>

                {/* Task Assignee */}
                <div>
                  <label className="text-sm font-medium">Task Assignee</label>

                  <AssigneePicker
                    className="w-full h-[40px]"
                    selected={selectedAssignees}
                    onSelect={(u) =>
                      setSelectedAssignees((prev) => [...prev, u])
                    }
                    onRemove={(id) =>
                      setSelectedAssignees((prev) =>
                        prev.filter((u) => u._id !== id),
                      )
                    }
                  />
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-4">
                {/* Task Type */}
                <div>
                  <label className="text-sm font-medium">Task Type</label>
                  <div className="relative w-full mt-1">
                    {/* Trigger */}
                    <div
                      className="w-full h-[40px] border border-gray-300 rounded-[8px] px-3 flex items-center justify-between cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                      onClick={() => setShowEditTaskType((prev) => !prev)}
                    >
                      <span className="capitalize text-[14px] text-gray-800">
                        {editTaskData.taskType}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`text-gray-500 transition-transform ${
                          showEditTaskType ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    {showEditTaskType && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowEditTaskType(false)}
                        />
                        <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-300 rounded-[8px] shadow-lg z-50 max-h-[180px] overflow-y-auto">
                          {[
                            "requirements",
                            "design",
                            "frontend",
                            "backend",
                            "testing",
                            "deployment",
                            "others",
                          ].map((item) => (
                            <div
                              key={item}
                              className="px-3 py-3 hover:bg-blue-500 hover:text-white cursor-pointer capitalize text-[14px] transition-colors border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                setEditTaskData({
                                  ...editTaskData,
                                  taskType: item,
                                });
                                setShowEditTaskType(false);
                              }}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Estimated Hours */}
                <div>
                  <label className="text-sm font-medium">Estimated Hours</label>
                  <input
                    type="number"
                    min={1}
                    max={getMaxEstimatedHours(
                      editTaskData.taskStartDate,
                      editTaskData.taskDeadline,
                    )}
                    className="w-full h-[40px] mt-1 border p-2 rounded-md"
                    value={editTaskData.estimatedHours}
                    onChange={(e) => {
                      const maxHours = getMaxEstimatedHours(
                        editTaskData.taskStartDate,
                        editTaskData.taskDeadline,
                      );
                      const raw = e.target.value;
                      const capped =
                        raw !== "" && Number(raw) > maxHours
                          ? String(maxHours)
                          : raw;
                      setEditTaskData({
                        ...editTaskData,
                        estimatedHours: capped,
                      });
                    }}
                  />
                  {editTaskData.taskStartDate && editTaskData.taskDeadline && (
                    <p className="text-xs text-gray-400 mt-1">
                      Max{" "}
                      {getMaxEstimatedHours(
                        editTaskData.taskStartDate,
                        editTaskData.taskDeadline,
                      )}{" "}
                      hours (8 hours/day) for the selected dates.
                    </p>
                  )}
                </div>

                {/* End Date */}
                <div>
                  <label className="text-sm font-medium">End Date</label>

                  <div className="relative mt-1">
                    <div className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 z-10">
                      <Icons.Calendar />
                    </div>

                    <DatePicker
                      wrapperClassName="w-full"
                      selected={
                        editTaskData.taskDeadline
                          ? new Date(editTaskData.taskDeadline)
                          : null
                      }
                      onChange={(date) =>
                        setEditTaskData({
                          ...editTaskData,
                          taskDeadline: date ? date.toISOString() : "",
                        })
                      }
                      className="w-full h-[40px] border p-0 pl-[55px] pr-3 rounded-md text-[14px] outline-none"
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select end date"
                      minDate={
                        editTaskData.taskStartDate
                          ? new Date(editTaskData.taskStartDate)
                          : null
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="w-full mt-2 border p-3 rounded-md resize-none"
                rows="3"
                value={editTaskData.description}
                onChange={(e) =>
                  setEditTaskData({
                    ...editTaskData,
                    description: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
                onClick={() => {
                  setShowEditTaskModal(false);
                  setEditingTaskColIndex(null);
                  setEditingTaskIndex(null);
                  setSelectedAssignees([]);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-[#4253C7] text-white rounded-md hover:bg-[#4253C7]"
                onClick={handleSaveEditTask}
                disabled={isEditingTask}
              >
                {isEditingTask ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showComments && getCommentTask() && (
        <div
          className="fixed inset-0 bg-black/20 z-[9999] flex items-center justify-center"
          onClick={() => {
            setShowComments(false);
            setCommentAnchorRect(null);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={getCommentPanelStyle(commentAnchorRect)}
            className="bg-white w-[95vw] max-w-[447px] h-[307px] max-h-[90vh] rounded-[16px] border border-black p-5 flex flex-col shadow-xl"
          >
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 flex-shrink-0">
              <h2 className="text-[20px] font-medium">Comment</h2>

              <button
                onClick={() => {
                  setShowComments(false);
                  setCommentAnchorRect(null);
                  setCommentTaskIndex(null);
                  setCommentColumnIndex(null);
                  setCommentInput("");
                  setUploadedFiles([]);
                }}
                className="text-gray-500 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Dynamic Comments — grows/scrolls to fill the space between
                the header and the input row, which stays pinned to the
                bottom of the card. */}
            <div
              ref={commentsModalListRef}
              className="space-y-4 mt-5 flex-1 min-h-0 overflow-y-auto"
            >
              {getCommentTask()?.comments &&
              getCommentTask().comments.length > 0 ? (
                getCommentTask().comments.map((comment, ci) => (
                  <div key={comment._id || ci} className="flex gap-3">
                    <div className="w-12 h-12 rounded-full border flex items-center justify-center flex-shrink-0 p-0.5">
                      <CommentAvatar author={comment.author} size={40} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <p className="font-semibold text-sm">
                          {comment.author?.name || comment.author || "User"}
                        </p>
                        <p className="text-gray-400 text-xs">
                          {formatRelativeTime(comment.createdAt) ||
                            comment.timestamp}
                        </p>
                      </div>

                      {showMentionBox && (
                        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white border rounded-lg shadow-lg z-50">
                          {getMentionFilteredUsers().map((user) => (
                            <button
                              key={user._id}
                              type="button"
                              onClick={() => insertMention(user)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100"
                            >
                              {user.name}
                            </button>
                          ))}
                        </div>
                      )}

                      {editingCommentId === comment._id ? (
                        <div className="mt-2">
                          <textarea
                            value={editingCommentText}
                            onChange={(e) =>
                              setEditingCommentText(e.target.value)
                            }
                            className="w-full border rounded-md p-2 text-sm resize-none"
                            rows={3}
                          />

                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              className="px-3 py-1 border rounded-md text-sm"
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditingCommentText("");
                              }}
                            >
                              Cancel
                            </button>

                            <button
                              className="px-3 py-1 bg-[#4253C7] text-white rounded-md text-sm"
                              onClick={async () => {
                                const task = getCommentTask();
                                if (!task) return;

                                const text = editingCommentText.trim();
                                if (!text) return;

                                const listId = columns[commentColumnIndex]?._id;
                                if (!listId) return;

                                try {
                                  await updateComment(
                                    selectedProject._id,
                                    listId,
                                    task._id,
                                    comment._id,
                                    { text },
                                  );
                                  await loadBoard(selectedProject._id);
                                  setEditingCommentId(null);
                                  setEditingCommentText("");
                                } catch (err) {
                                  showError(
                                    err?.response?.data?.message ||
                                      "Unable to update comment.",
                                  );
                                }
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm mt-1 break-words">
                          {comment.text}
                        </p>
                      )}
                      {comment.attachments &&
                        comment.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {comment.attachments.map((attachment) => (
                              <a
                                key={attachment._id}
                                href={getSafeUrl(attachment.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-1 text-xs hover:bg-blue-100 transition"
                              >
                                <span>{getFileIcon(attachment.fileType)}</span>
                                <span className="truncate max-w-[100px]">
                                  {attachment.fileName}
                                </span>
                              </a>
                            ))}
                          </div>
                        )}
                    </div>

                    {/* Edit/Delete menu — available to all roles */}
                    <div className="relative flex-shrink-0 ml-auto">
                      <button
                        className="text-gray-500 px-2 hover:text-gray-700"
                        onClick={() =>
                          setActiveCommentMenu(
                            activeCommentMenu === comment._id
                              ? null
                              : comment._id,
                          )
                        }
                      >
                        <MoreHorizontal size={18} />
                      </button>

                      {activeCommentMenu === comment._id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setActiveCommentMenu(null)}
                          />
                          <div className="absolute right-0 top-6 bg-white border rounded-md shadow-lg z-50 w-[100px]">
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                              onClick={() => {
                                handleEditComment(comment);
                                setActiveCommentMenu(null);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-gray-100"
                              onClick={() => {
                                setCommentPendingDelete({
                                  comment,
                                  task: getCommentTask(),
                                  listId: columns[commentColumnIndex]?._id,
                                });
                                setActiveCommentMenu(null);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}

                      {commentPendingDelete?.comment._id === comment._id && (
                        <CommentDeletePopup
                          containerRef={commentsModalListRef}
                          onCancel={() => setCommentPendingDelete(null)}
                          onConfirm={() => {
                            handleDeleteComment(
                              commentPendingDelete.comment,
                              commentPendingDelete.task,
                              commentPendingDelete.listId,
                            );
                            setCommentPendingDelete(null);
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No comments yet
                </p>
              )}
            </div>

            {/* Input — pinned to the bottom of the card */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t flex-shrink-0">
              <div className="w-12 h-12 rounded-full border flex items-center justify-center flex-shrink-0 p-0.5">
                <CommentAvatar author={user} size={40} />
              </div>

              <input
                type="text"
                placeholder="Write a comment"
                className="flex-1 h-[40px] border rounded-md px-3 outline-none"
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && commentInput.trim()) {
                    handlePostComment();
                  }
                }}
              />

              <button
                className="bg-[#4253C7] text-white px-5 h-[40px] rounded-md hover:bg-blue-600 transition"
                onClick={handlePostComment}
                disabled={isPostingComment || !commentInput.trim()}
              >
                {isPostingComment ? "..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ERROR POPUP — in-app replacement for window.alert() */}
      {errorPopup && (
        <div className="fixed inset-0 bg-black/30 z-[10000] flex items-center justify-center p-4">
          <div className="w-full max-w-[420px] bg-white rounded-xl shadow-2xl p-6 relative text-center">
            <button
              onClick={() => setErrorPopup("")}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-red-500" size={22} />
            </div>
            <h2 className="text-base font-bold text-black mb-2">Notice</h2>
            <p className="text-sm text-gray-500 mb-6 whitespace-pre-line text-left">
              {errorPopup}
            </p>
            <button
              onClick={() => setErrorPopup("")}
              className="w-full h-10 bg-[#4253C7] text-white rounded-lg text-sm font-semibold hover:bg-[#3644b0]"
            >
              Okay
            </button>
          </div>
        </div>
      )}

      {/* DELETE TASK CONFIRMATION */}
      {taskPendingDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white w-[95vw] md:w-[400px] p-6 rounded-xl relative">
            <h2 className="text-lg font-semibold mb-2">Delete Task</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this task? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded-md text-sm hover:bg-gray-50 transition"
                onClick={() => setTaskPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 transition"
                onClick={() => {
                  handleDeleteTask(
                    taskPendingDelete.colIndex,
                    taskPendingDelete.taskIndex,
                  );
                  setTaskPendingDelete(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVITY LOGS PANEL */}
      {showActivityLogs && (
        <div className="fixed inset-0 z-[999] flex justify-end ">
          <div
            className="absolute inset-0 bg-black/20"
            onClick={() => setShowActivityLogs(false)}
          />
          <div className="relative bg-white w-full sm:w-[35vw] sm:min-w-[280px] h-full shadow-xl flex flex-col overflow-hidden rounded-l-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5">
              <h2 className="text-[17px] font-bold text-black">
                Activity Logs
              </h2>
              <button
                onClick={() => setShowActivityLogs(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex items-start gap-2 px-5 mt-3">
              <Icons.Logsfile />
              <div>
                <p className="font-semibold text-sm text-black">
                  Project {logsProject?.name || selectedProject?.name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Created On{" "}
                  {formatLogTimestamp(
                    logsProject?.createdAt || selectedProject?.createdAt,
                  ) || "—"}
                </p>
              </div>
            </div>

            {/* Search + Delete Logs */}
            <div className="flex items-center gap-2 px-5 mt-4">
              <div className="relative flex-1">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  placeholder="Search Logs..."
                  className="w-full h-9 pl-8 pr-3 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#4253C7]"
                />
              </div>
              {userRole === "admin" && (
                <button
                  type="button"
                  onClick={() => setLogsDeleteStep(1)}
                  disabled={(logs || []).length === 0}
                  className="h-9 px-3 border border-red-500 text-red-500 text-xs w-[156px] font-semibold rounded-lg hover:bg-red-50 whitespace-nowrap disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  Delete Logs
                </button>
              )}
            </div>

            {/* Log list */}
            <div className="flex-1 overflow-y-auto px-5 mt-3 border-t">
              {isLogsLoading ? (
                <p className="text-xs text-gray-400 text-center py-6">
                  Loading logs...
                </p>
              ) : logsError ? (
                <p className="text-xs text-red-500 text-center py-6">
                  {logsError}
                </p>
              ) : filteredLogs.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">
                  No activity logs found.
                </p>
              ) : (
                <div className="relative space-y-4 pb-2">
                  <div className="absolute left-[5px] top-1.5 bottom-1.5 w-0.5 bg-[#4253C7]" />
                  {filteredLogs.map((log) => (
                    <div
                      key={log._id}
                      className="relative flex items-start gap-2 pl-4"
                    >
                      <span className="absolute left-[1px] top-2 w-2 h-2 rounded-full bg-[#4253C7]" />
                      <div className="w-6 h-6 rounded-full bg-[#F4A340] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                        {getProfileImageUrl(log.actor) ? (
                          <img
                            src={getProfileImageUrl(log.actor)}
                            alt={log.actor?.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          getInitials(log.actor?.name)
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-black">
                          <span className="font-semibold">
                            {log.actor?.name || "Unknown"}
                          </span>{" "}
                          <span className="text-gray-400">
                            {formatLogTimestamp(log.createdAt)}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 break-words">
                          {log.action}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {logsPagination && logsPagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 px-5 py-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() =>
                    handleLogsPageChange(logsPagination.currentPage - 1)
                  }
                  disabled={logsPagination.currentPage <= 1}
                  className="w-5 h-7 flex items-center border border-gray-200 justify-center rounded text-gray-400 hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from(
                  { length: logsPagination.totalPages },
                  (_, i) => i + 1,
                ).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleLogsPageChange(p)}
                    className={`w-7 h-7 flex items-center justify-center rounded-md text-xs font-medium ${
                      p === logsPagination.currentPage
                        ? "bg-[#4253C7] text-white"
                        : "text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    handleLogsPageChange(logsPagination.currentPage + 1)
                  }
                  disabled={
                    logsPagination.currentPage >= logsPagination.totalPages
                  }
                  className="w-5 h-7 flex items-center border border-gray-200 justify-center rounded text-gray-400 hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DELETE LOGS — STEP 1: "Delete Logs?" */}
      {logsDeleteStep === 1 && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white w-full max-w-[453px] min-h-[395px] rounded-[8px] shadow-2xl p-6 text-center flex flex-col justify-center">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="text-red-500" size={30} />
            </div>
            <h2 className="text-2xl font-bold text-black mb-3">Delete Logs?</h2>
            <p className="text-base text-gray-500 mb-8">
              Are you sure you want to delete all activity logs for{" "}
              <span className="font-semibold text-black">
                Project {logsProject?.name || selectedProject?.name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setLogsDeleteStep(0)}
                className="flex-1 h-12 border border-red-500 text-red-500 rounded-lg text-base font-medium hover:bg-red-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setLogsDeleteStep(2)}
                className="flex-1 h-12 bg-red-500 text-white rounded-lg text-base font-semibold hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE LOGS — STEP 2: "Confirm Permanent Deletion?" (type-to-confirm) */}
      {logsDeleteStep === 2 && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white w-full max-w-[453px] min-h-[395px] rounded-[8px] shadow-2xl p-6 text-center flex flex-col justify-center">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <Trash2 className="text-red-500" size={26} />
            </div>
            <h2 className="text-2xl font-bold text-black mb-3">
              Confirm Permanent Deletion?
            </h2>
            <p className="text-base text-gray-500 mb-5">
              To permanently delete all activity logs for{" "}
              <span className="font-semibold text-black">
                Project {logsProject?.name || selectedProject?.name}
              </span>
              , type the project name below:
            </p>
            <input
              type="text"
              autoFocus
              value={logsDeleteConfirmText}
              onChange={(e) => {
                setLogsDeleteConfirmText(e.target.value);
                setLogsDeleteError("");
              }}
              placeholder="Type the project name..."
              className="w-full h-12 border border-gray-200 rounded-lg px-4 text-base outline-none focus:border-[#4253C7] mb-3"
            />
            {logsDeleteError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4 text-left">
                <AlertTriangle
                  size={16}
                  className="text-red-500 flex-shrink-0 mt-0.5"
                />
                <p className="text-sm text-red-600">{logsDeleteError}</p>
              </div>
            )}
            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => {
                  setLogsDeleteStep(1);
                  setLogsDeleteConfirmText("");
                  setLogsDeleteError("");
                }}
                className="flex-1 h-12 border border-red-500 text-red-500 rounded-lg text-base font-medium hover:bg-red-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={handleConfirmPermanentDeletion}
                disabled={isDeletingLogs}
                className="flex-1 h-12 bg-red-500 text-white rounded-lg text-base font-semibold hover:bg-red-600 disabled:opacity-60"
              >
                {isDeletingLogs ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Kanban;
